/**
 * Design tokens and MUI component overrides for the Novus theme.
 *
 * Values extracted from the Figma file: Novus — Product Offers Page.
 * Consumed by theme.ts via spread into the createTheme call.
 * Individual @emotion/styled components should reference these tokens
 * through `theme.novus.*` for consistency.
 */

import { MS } from '@config/theme/typography.config';
import { theme as symphonaTheme } from '@symphona/ui-kit';

// ---------------------------------------------------------------------------
// Custom design tokens
// ---------------------------------------------------------------------------

// Per-band column rule for the Theme-Packages grid in TVAddOnsStep. SC-12441:
// theme cards need a 350px min width to contain their channel-logo row — below
// that the logos overflow outside the card. On the Mobile and Bigger-Mobile
// bands (xs/sm, <=768) the cards therefore only pair up once two 350px cards
// fit; narrower than that they drop to a single full-width column. The md rule
// (>=769) is kept byte-identical to the pre-migration inline rule so desktop
// rendering is unchanged: a hard repeat(4, ...) would alter column widths at
// non-4-fit dialog widths.
export const themePackageGridColumns = {
  xs: 'repeat(auto-fill, minmax(min(350px, 100%), 1fr))',
  sm: 'repeat(auto-fill, minmax(min(350px, 100%), 1fr))',
  md: 'repeat(auto-fit, minmax(min(292px, 100%), 1fr))',
} as const;

export const themePackageGridGap = '36px';

// Individual-Channels grid (TVAddOnsStep). MUST stay a per-band responsive OBJECT
// (not a bare string): MUI emits a plain sx value BEFORE the styled base class, so a
// bare string loses to the shared CardGrid base columns
// (`repeat(auto-fill, minmax(min(380px,100%),1fr))`) and the card ends up left-aligned
// in a full-width 1fr cell. Only the breakpoint-object form wins (same reason the gap
// override uses `{ xs }`).
//
// SC-12535: phones (xs) force EXACTLY two columns so "two cards on mobile" holds all the
// way down. `repeat(2, minmax(0, 160px))` caps each card at the 160px Figma width where
// there is room and lets both shrink below it on the narrowest phones (~132px at 320)
// rather than collapsing to one column — an `auto-fit` track fits a second fixed 160px
// card only above ~374px, which is why 373px dropped to a single card. From sm up the
// dialog has room, so `auto-fit` centres as many 160/192px cards as fit; the grid's
// `justifyContent` balances the leftover space and `min(..., 100%)` guards a sub-card
// viewport.
export const individualChannelGridColumns = {
  xs: 'repeat(2, minmax(0, 160px))',
  sm: 'repeat(auto-fit, minmax(min(160px, 100%), 160px))',
  md: 'repeat(auto-fit, minmax(min(192px, 100%), 192px))',
} as const;

export const individualChannelGridGap = '16px';

// Plan-card grid (PlansSection). One column on phones; from tablet up, fit as
// many >=330px cards per row as the width allows. The md string is kept
// byte-identical to the pre-migration inline rule so >=md rendering is
// unchanged. The single-plan toggle stays a data-driven component choice.
export const planGridColumns = {
  xs: '1fr',
  md: 'repeat(auto-fit, minmax(330px, 1fr))',
} as const;

export const planGridGap = '50px';

export const novusTokens = {
  /**
   * Tenant logo <img> sizing caps (NavTopBar / CheckoutHeader). A wide wordmark logo
   * has no slack the way the bundled square SvgIcon fallback does (that one letterboxes
   * inside inheritViewBox), so both width and height need explicit px caps per bar —
   * a height cap alone leaves width unbounded inside the shrink-wrapping IconButton.
   * NavTopBar's bar is flat 67px at every width, so its logo cap is flat too. CheckoutHeader's
   * fontSize scales 90px->130px at the sm breakpoint (novusBreakpoints.sm) for its SvgIcon
   * fallback; these mirror that same two-step scale for the img path.
   */
  navLogoImgMaxHeight: '40px',
  navLogoImgMaxWidth: '160px',
  checkoutLogoImgMaxHeightMobile: '28px',
  checkoutLogoImgMaxWidthMobile: '120px',
  checkoutLogoImgMaxHeight: '40px',
  checkoutLogoImgMaxWidth: '160px',

  /** Core brand colors */
  primaryBrandBlue: '#001884',
  accentCyan: '#00B0F0',
  accentCyanBright: '#00CDFF',
  headingBlue: '#03288F',

  /** Category section backgrounds — alternate based on visible order */
  sectionBgWhite: '#FFFFFF',
  sectionBgBlue: '#EBF5FF',
  bgCyanLight: '#BFF2FF',
  bgCyanLighter: '#E8FAFF',
  bgCyanDark: '#80E6FF',

  /** Plan card */
  cardBorderRadius: '20px',
  cardPadding: '30px',
  cardBoxShadow: '1px 1px 10px 0px rgba(0, 0, 0, 0.15)',
  cardBorderDefault: '1px solid #E0E0E0',
  cardBorderSelected: {
    Internet: '2px solid #001884',
    Video: '2px solid #001884',
    Phone: '2px solid #001884',
  },
  cardSelectedBadgeBg: {
    Internet: '#001884',
    Video: '#001884',
    Phone: '#001884',
  },

  /**
   * Plan-card tenant overrides (CPQ Branding -> Plan Card).
   *
   * Every one of these defaults to the empty string, which means "inherit what the
   * card already does". The variants below read them as `planCardX || <existing
   * expression>`, so with no tenant theme the rendered result is byte-identical to
   * what it was before these tokens existed -- the shared `card*` tokens for the
   * flat properties, and `2px solid primaryBrandBlue` for the selected states.
   *
   * They are deliberately NOT defaulted to `cardBorderSelected`/`cardSelectedBadgeBg`
   * above (which are static '#001884'): the selected border and badge follow the
   * tenant's brand colour today, and hardcoding them here would regress any tenant
   * that sets only `topNavBar.colorBrand`.
   */
  /**
   * Tenant-only overrides, all defaulting to '' so an un-themed storefront renders
   * exactly as before. Each is read as `token ? <tenant rule> : <existing rule>` by
   * the variant that owns it.
   *
   * They exist as separate tokens because the obvious candidates could not be reused
   * without moving a default:
   *  - `equipmentCardBorderHover` / `addOnCardBorderHover` are read by the
   *    themePackageCard and individualChannelCard variants, and their defaults
   *    ('2px solid #001884' / '3px solid #001884') do not match the equipment and
   *    add-on cards' current hover, which only swaps the outline COLOUR to navy.
   *  - `cartFooterBg` / `cartFooterColor` default to '#1A1A2E' / '#FFFFFF' but the
   *    cartDrawerFooter has always painted hardcoded white, so adopting them directly
   *    would turn the footer dark for every tenant that never set one.
   */
  equipmentCardHoverOutline: '',
  addOnCardHoverOutline: '',
  cartFooterSurfaceBg: '',
  cartFooterTextColor: '',

  /** Cart Drawer -> "Total Label". Empty = inherit quantityLabelColor, as before. */
  cartTotalLabelColor: '',
  planCardBorderRadius: '',
  planCardPadding: '',
  planCardBoxShadow: '',
  planCardBoxShadowHover: '',
  planCardBorderDefault: '',
  planCardBorderSelectedInternet: '',
  planCardBorderSelectedVideo: '',
  planCardBorderSelectedPhone: '',
  planCardBadgeBgInternet: '',
  planCardBadgeBgVideo: '',
  planCardBadgeBgPhone: '',

  /** Highlight bullet icon */
  highlightIconColor: '#00B0F0',
  highlightIconSize: '20px',

  /**
   * Discount / promotion badge background. Default matches the old hardcoded
   * addOnDiscountBadgeBg value ('#00cdff') so a tenant with no themeConfig at all
   * renders identically to before this token became the discountBadge variant's
   * actual colour source — only a tenant that explicitly sets discount.bgDiscount
   * gets a different colour now.
   */
  discountBadgeBg: '#00cdff',
  discountBadgeBorder: '1px solid #FF9800',
  /**
   * Default matches primaryBrandBlue's default ('#001884') — NOT MuiTypography's
   * `defaultProps.color` ('#2d3a47') as an earlier version of this comment assumed.
   * That assumption was based on a vitest/jsdom render: jsdom's cascade implementation
   * (jsdom/lib/jsdom/living/helpers/style-rules.js) does not implement CSS specificity —
   * it resolves conflicts by rule insertion order only. In a real browser, the discount
   * tag's ancestor->descendant selector (2 classes, specificity 0,2,0) beats
   * MuiTypography's single-class default (0,1,0) regardless of order, so the tag
   * actually rendered PlanCard-price's colour (primaryBrandBlue) all along — the
   * jsdom probe reported the opposite because it isn't a specificity-accurate
   * environment. Matching that real historical value here means a tenant with no
   * themeConfig at all still renders the same colour as before; only a tenant that
   * explicitly sets discount.colorDiscountTag now gets a different one.
   */
  discountTagColor: '#001884',
  discountMessageColor: '#424242',

  /** Action buttons */
  selectPlanBg: '#001884',
  selectPlanHoverBg: '#000F5C',
  selectPlanColor: '#FFFFFF',
  editPlanBg: 'transparent',
  editPlanHoverBg: 'transparent',
  editPlanBorder: '1px solid #DC9FFF',
  editPlanColor: '#001884',
  //hover color change is required for non novus customer. since default values are for novus hence hover color is kept same
  editPlanHoverColor: '#001884',

  /** Sticky cart footer */
  cartFooterBg: '#1A1A2E',
  cartFooterColor: '#FFFFFF',
  cartFooterHeight: '72px',
  novusFooterHeight: '79px',

  novusFabSize: 67,
  novusFabOffset: 20,
  novusFabBannerOffset: 85,
  navBarCartHoverBg: '#ffffff14',
  checkoutBg: '#001884',
  checkoutHoverBg: '#000F5C',
  checkoutDisabledBg: '#9E9E9E',

  /** Address header */
  addressHeaderGradient: 'linear-gradient(135deg, #4A148C 0%, #1565C0 100%)',
  addressHeaderBg: '#00CEFF',
  addressHeaderColor: '#FFFFFF',
  /** Translucent white surface of the address card sitting on the hero band. */
  addressCardBg: 'rgba(255, 255, 255, 0.8)',
  changeAddressBtnColor: '#FFD54F',

  // ── Configuration Dialog (SC-9699) ─────────────────────────────────────

  /** Dialog container — responsive, max dimensions from Figma */
  dialogMaxWidth: 1337,
  dialogMaxHeight: 916,
  dialogBorderRadius: '25px',
  dialogBg: '#ffffff',

  /** Dialog sections */
  dialogHeaderHeight: 86,
  dialogFooterHeight: 76,
  dialogSectionBorder: '1px solid #dfe4ed',
  dialogSectionPadding: '0 30px',
  dialogContentPadding: '24px 30px',
  dialogContentGap: 16,
  dialogFooterMobileGap: 12,
  // SC-12441: on the Mobile band the add-ons footer releases the fixed 76px height
  // (it stacks a summary line above the button row), so it needs its own vertical
  // padding to keep the same breathing room the equipment footer gets from being
  // centred in its fixed height — without it the buttons sit flush on the viewport
  // bottom edge and read as cut off. Horizontal stays 30px to match dialogSectionPadding.
  dialogFooterMobilePadding: '16px 30px',

  /** Close button */
  closeButtonSize: 20,
  closeButtonHoverOpacity: 0.7,

  modalBackdropBlur: 'blur(100px)',

  /** Right-anchored side drawer / modal paper width above the Mobile band (R10). */
  sideModalWidth: 676,

  /** Theme-Packages grid (TVAddOnsStep) — per-band columns + gap. */
  themePackageGridColumns,
  themePackageGridGap,

  /** Individual-Channels grid (TVAddOnsStep) — per-band columns + gap. */
  individualChannelGridColumns,
  individualChannelGridGap,

  /** Plan-card grid (PlansSection) — responsive columns + gap. */
  planGridColumns,
  planGridGap,

  /** Subheader / error banner */
  errorBannerGap: 8,

  /** Colors — configuration dialog */
  navy: '#001884',
  purplePrimary: '#dc9fff',
  purpleCheck: '#b83bff',
  cyanSelectedBorder: '#40daff',
  cyanAccent: '#00cdff',
  defaultBorder: '#ced3df',
  defaultHoverBorder: '#a0a8b8',
  headerBorder: '#dfe4ed',
  errorRed: '#cf1010',
  errorTextRed: '#CF1010',
  errorBannerBg: '#ffe6e6',
  errorIcon: '#e03b24',
  placeholderBg: '#f5f5f5',
  quantityLabelColor: '#2d3a47',

  /** Shared card / button tokens */
  cardTransition: 'border 0.2s ease',
  buttonRadius: '8px',
  buttonTransition: 'opacity 0.2s ease',
  buttonHoverOpacity: 0.85,

  /** Equipment card */
  equipmentCardRadius: '10px',
  equipmentCardBorderDefault: '2px solid #ced3df',
  equipmentCardBorderSelected: '3px solid #00cdff',
  equipmentCardBorderHover: '2px solid #001884',
  equipmentImageSize: { width: 125, height: 118 },
  equipmentCardPadding: '16px 20px',
  equipmentCardGap: 20,
  equipmentCardDetailsGap: 8,

  /** Add-on card */
  addOnCardRadius: '10px',
  addOnCardPadding: '15px',
  addOnCardGap: 15,
  addOnCardContentGap: 10,
  addOnCardBorderDefault: '1px solid #ced3df',
  addOnCardBorderSelected: '3px solid #40daff',
  addOnCardBorderHover: '3px solid #001884',
  addOnCheckboxSize: 20,
  addOnDiscountBadgePadding: '5px 10px',
  addOnDiscountBadgeHeight: '30px',

  /** Quantity counter */
  quantityCounterBorder: '1px solid #2d3a47',
  quantityCounterRadius: '4px',

  /** Novus Purple Button */
  novusPurpleBg: '#dc9fff',
  novusPurpleColor: '#001884',
  novusPurpleOutlinedBorder: '1px solid #dc9fff',
  novusPurpleHoverBg: '#faf0ff',
  actionButtonPadding: '10px 32px',
  skipButtonPadding: '10px 24px',
  btnPurpleBg: '#DC9FFF',
  btnPurpleHoverBg: '#DC7FFF',
  novusPurpleContainedHoverBg: '#EDCEFF',
  /** Hover fill for the check-availability CTA — distinct from btnPurpleHoverBg (#DC7FFF). */
  btnPurpleCtaHoverBg: '#c480f5',
  /**
   * Text colour for filled CTA buttons on a `btnPurpleBg` fill (e.g. checkAvailabilityCta).
   * Was hardcoded to primaryBrandBlue, which reads fine against the default light-purple
   * fill but goes invisible once a tenant sets both its brand accent (topNavBar.colorBrand
   * -> primaryBrandBlue) and its button fill (addressHeader.btnPurpleBg) to the same colour —
   * text-on-background of identical hue. addressHeader.btnTextColor already carries the
   * config author's intended contrasting colour (e.g. white); this token gives it a home.
   */
  btnTextColor: '#001884',

  /** Category Filter Bar */
  borderDisabled: '#9AA2A5',
  borderBlack: '#000000',

  /** Plan Card Link */
  linkColor: '#8800D3',
  linkHoverColor: '#6A00A3',

  /** Warning Box */
  warningBg: '#FFF3E0',
  warningText: '#936A00',
  warningIconColor: '#E7BB09',

  /** Warning Dialog */
  warningDialogIconColor: '#d4a017',

  /** Address Header */
  changeAddressBtnHoverBg: '#FFC107',

  /** Order Summary */
  orderSummaryDivider: '#2FD6FF',
  discountGreen: '#38780A',

  /** Shopping Cart */
  cartSectionDivider: '#8A96AC',
  cartFooterShadow: '1px 1px 10px 2px #0000001A',
  // Landscape / short-viewport compaction for the sticky cart-drawer footer
  // (cartDrawerFooter variant). At <=520px tall the base clamp(20,5vw,30) block
  // padding + 10px inter-row gap make the footer tall enough to starve the item
  // list's scroll region — the same class of bug the configurePlanDialog
  // shortViewportDown branch fixes for the add-ons dialog. These tighten only the
  // footer's own vertical padding and inter-row gap so the reclaimed height flows
  // to the scrollable list; horizontal padding and every footer line stay intact.
  cartDrawerFooterShortViewportPaddingBlock: '10px',
  cartDrawerFooterShortViewportGap: '6px',

  /** TV Channel Logos */
  channelLogoBorder: '#DFE4ED',
  channelLogoBorderHover: '#03288F',
  channelTooltipShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',

  /** Service Address card outline */
  serviceCardBorder: '#DADEE7',

  /** Muted text */
  mutedText: '#8A94A6',

  /** File-preview thumbnail off-white fill (PhoneNumberStep) */
  fileThumbnailBg: '#F9FAFB',

  /** Coupon / Promotional Code (SC-11100) */
  couponAppliedAccent: '#008A05',
  promoLearnMoreUrl: 'https://www.novusnow.ca/promos/#refer-a-friend',

  /** Checkout step accordion */
  stepCardShadow: '0 4px 16px 2px #00000021',

  /** Full checkout page background (CheckoutPageWrapper). Default = ui-kit colors.pattensBlue. */
  checkoutPageBg: '#E1F6FF',

  /** Cart drawer surfaces (ShoppingCartDrawer.styles.ts). Defaults = the previous literals. */
  cartItemBorder: '#DFE4ED',
  cartSurfaceWhite: '#FFFFFF',
  cartExpandableBg: '#F2F4F6',
  cartExpandableSectionBg: '#F9FAFB',
  cartVerticalDivider: '#CED3DF',

  /** Radio / checkbox checked colour (MuiRadio, MuiCheckbox). Default = previous literal. */
  checkboxColor: '#b83bff',
  /** Disabled text on the checkoutDrawer button. Default = previous literal. */
  checkoutDrawerDisabledColor: '#FFFFFF',
  /** themePackageCard box-shadow. Default = previous literal. */
  themePackageCardShadow: '0px 6px 20px rgba(0,0,0,0.13)',
  /** individualChannelCard box-shadow. Default = previous literal. */
  individualChannelCardShadow: '1px 1px 10px 2px rgba(0,0,0,0.1)',
  /** DialogActions footer shadow. Default = previous literal. */
  dialogActionsShadow: '1px 1px 10px 2px #0000001A',
  /** Warning dialog paper box-shadow. Default = previous literal. */
  warningDialogShadow: '1px 1px 10px 2px rgba(0, 0, 0, 0.1)',
  /** Autocomplete option focus / selected / tag background. Default = previous literal. */
  autocompleteOptionBg: '#F3E5F5',
  /** Autocomplete input outline border. Default = previous literal. */
  inputBorderColor: '#c4c4c4',
  /** Autocomplete popper box-shadow. Default = previous literal. */
  autocompleteShadow: '1px 1px 10px 2px rgba(0, 0, 0, 0.10)',
  /** Optional-label / helper text (TextFieldLabel). Default = previous literal. */
  textSubtle: '#767676',

  /** Order Confirmation page */
  orderConfirmationBg: '#D9F4FB',
  orderConfirmationCardBorder: '#B2EBF2',
  orderConfirmationHeading: '#1A1A1A',
  orderConfirmationBody: '#4A4A4A',
} as const;

export type NovusTokens = typeof novusTokens;

// Single source of truth for the FAB bottom-clearance every full-bleed page
// surface carries. Derived from the FAB dimensions (size + offset + breathing
// room), never a divorced hardcoded literal (PD-2): 67 + 20 + 13 = 100, which
// over-clears the bare footer height instead of under-clearing it. Both
// theme.ts (mixins.page.fabClearance) and the pageFullBleed Paper variant below
// bind to this same const so the clearance can never drift between them.
export const pageFabClearance = `${novusTokens.novusFabSize + novusTokens.novusFabOffset + 13}px`;

// Per-band horizontal page gutter shared by every full-bleed band so their centred
// Container content lines up on one left/right edge at each width. Mirrors the
// `mixins.page.gutter` values in theme.ts; the cartFooterBar Paper variant below
// binds here (it lives in this plain-object override where theme.mixins is not in
// scope) so its padding stays token-driven instead of a frozen magic-pixel set.
export const pageGutter = { xs: '15px', sm: '20px', md: '24px', lg: '40px', xl: '100px' } as const;

// ---------------------------------------------------------------------------
// Custom breakpoint scale (R6)
// ---------------------------------------------------------------------------

/**
 * The five Novus responsiveness bands mapped onto MUI's xs/sm/md/lg/xl keys.
 * Single source of truth: theme.ts feeds these into createTheme so
 * `theme.breakpoints` regenerates up/down, and the card variants below derive
 * their media boundaries from the same values (so nothing drifts).
 *
 *   Mobile 0–480 (xs) | Bigger Mobile 481–768 (sm) | Tablet 769–1024 (md) |
 *   Bigger Tablet/Desktop 1025–1440 (lg) | 2K+ 1441+ (xl)
 */
export const novusBreakpoints = {
  xs: 0,
  sm: 481,
  md: 769,
  lg: 1025,
  xl: 1441,
} as const;

// ---------------------------------------------------------------------------
// MUI component overrides scoped to Novus theme
// ---------------------------------------------------------------------------

// Shared visual contract for the plan cards (multi-plan grid + single-plan row).
// The selected border is uniform across categories for Novus today; it is driven
// by the brand navy token so a tenant theme can re-source it.
// Single-plan row layout (and the section-divider reveal) key off the Tablet band
// start; the inner padding drops on the Mobile band. Both derive from
// novusBreakpoints so they stay identical to theme.breakpoints.up('md') /
// down('sm') and move with the scale instead of being frozen magic-pixel queries.
const tabletUp = `@media (min-width:${novusBreakpoints.md}px)`;
// Bigger-Mobile band start (sm = 481). The order-summary divider widens its
// horizontal inset from here up — the same boundary the old DIVIDER_SX keyed off
// via mx:{xs,sm}. Derived from novusBreakpoints so it tracks the scale, not a frozen
// magic pixel; object xs/sm breakpoint syntax does NOT resolve inside a theme style.
const smUp = `@media (min-width:${novusBreakpoints.sm}px)`;
export const mobileDown = `@media (max-width:${novusBreakpoints.sm - 0.05}px)`;
// SC-12441: "all but desktop" boundary — below the Desktop (lg = 1025) band the
// add-ons footer stacks the summary and the button row onto separate lines; only
// true desktop keeps them on one row. down('lg') = max-width 1024.95, derived from
// novusBreakpoints so it tracks the scale.
export const belowDesktop = `@media (max-width:${novusBreakpoints.lg - 0.05}px)`;
// First HEIGHT-based boundary. The width scale above can't see a short-but-wide
// viewport (a phone in landscape is ~375px tall but ~667–844px wide, so it lands in
// the sm/md band and keeps the desktop centred-card dialog geometry). At that height
// the configure-plan dialog's fixed header + sub-heading + tabs + filter + footer
// consume the whole viewport and collapse its scrollable list to zero. Below this the
// dialog becomes a full-height scrollable sheet. 520px clears every phone-landscape
// orientation while staying well under common laptop window heights.
export const shortViewportDown = `@media (max-height:520px)`;
// Tablet band start (md = 769). Cards whose SC-12443 design stacks across BOTH the
// Bigger-Mobile (sm) and Mobile (xs) bands key their row→column flip off this — the
// complement of tabletUp — so the flip sits at 768.95px, matching theme.breakpoints.down('md').
const tabletDown = `@media (max-width:${novusBreakpoints.md - 0.05}px)`;
// Larger-band gutter steps for the cartFooterBar variant. Built from novusBreakpoints
// so they stay identical to theme.breakpoints.up('lg') / up('xl') and move with the
// scale (same precedent as tabletUp).
const desktopUp = `@media (min-width:${novusBreakpoints.lg}px)`;
const wideUp = `@media (min-width:${novusBreakpoints.xl}px)`;

// Shared layout for the two DialogHeader Paper variants. Both bars are 56px tall
// with 30px horizontal padding and lay out title + close on opposite ends; only the
// background differs (cyan bar vs. transparent). Paper ships a default elevation
// shadow, a borderRadius and a background-image overlay that the bare Box it replaces
// never had, so they are explicitly nulled here to keep the migration appearance-
// neutral. The title + close-icon borderBlack colours do NOT live in this variant: a
// Paper→descendant colour rule loses on the children's computed style to each child's
// own component default (Typography's default colour prop; IconButton's action.active).
// Both are re-homed onto the instance via the stable child classNames — the title in
// the MuiTypography styleOverrides `&&` block and the close icon in the MuiIconButton
// block below — same precedent as InfoBanner-text / QuantityCounter-valueText.
const dialogHeaderBase = {
  height: 56,
  paddingLeft: 30,
  paddingRight: 30,
  display: 'flex' as const,
  alignItems: 'center' as const,
  justifyContent: 'space-between' as const,
  flexShrink: 0,
  borderRadius: 0,
  boxShadow: 'none',
  backgroundImage: 'none',
};

export const buildNovusComponentOverrides = (tokens: typeof novusTokens) => {
  // CPQ Branding offers "Default Border" on the Plan Card as a COLOUR picker, so the
  // value arrives as a bare colour ('#d6dce8'), while the per-category selected
  // borders are free-text and arrive as a full shorthand ('2px solid #F97316').
  // `border: '#d6dce8'` is not a valid shorthand -- the browser drops it to
  // `0px none`, which silently removes the card border. Compose a shorthand when the
  // value is a bare colour and pass a full shorthand straight through.
  /**
   * Hover outline for the equipment / add-on cards.
   *
   * MUST always emit the same CSS key. createAppTheme deep-merges the rebuilt
   * component overrides onto `baseThemeOverrides`, which was built from this same
   * function using the static tokens -- so if the shape changed with the tokens
   * (`outlineColor` by default, `outline` when themed) the stale `outlineColor`
   * would survive the merge and win, silently overriding the tenant's colour.
   *
   * With no tenant value this returns the card's own default border with its colour
   * swapped to navy, which is byte-identical to the previous `outlineColor: navy`.
   */
  const hoverOutline = (tenant: string, base: string): string => {
    if (tenant) return tenant;
    const COLOUR = /#[0-9a-fA-F]{3,8}|rgba?\([^)]*\)/;
    return COLOUR.test(base) ? base.replace(COLOUR, tokens.navy) : `${base} ${tokens.navy}`;
  };

  const asBorder = (value: string | undefined, fallback: string): string => {
    const v = value?.trim();
    if (!v) return fallback;
    return /^(#|rgb|hsl)/i.test(v) ? `1px solid ${v}` : v;
  };

  const planCardBase = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
    backgroundColor: tokens.sectionBgWhite,
    // `planCardX || cardX`: the tenant's Plan Card value when CPQ Branding set one,
    // otherwise the shared card token exactly as before (planCard* default to '').
    borderRadius: tokens.planCardBorderRadius || tokens.cardBorderRadius,
    border: asBorder(tokens.planCardBorderDefault, tokens.cardBorderDefault),
    boxShadow: tokens.planCardBoxShadow || tokens.cardBoxShadow,
    padding: tokens.planCardPadding || tokens.cardPadding,
    overflow: 'visible',
    transition: 'border 0.2s ease, box-shadow 0.2s ease',
    // R13: inner padding reduces 30 -> 20 on the Mobile band.
    [mobileDown]: { padding: '20px' },
    '&:hover': {
      boxShadow: tokens.planCardBoxShadowHover || '1px 1px 14px 0px rgba(0, 0, 0, 0.20)',
    },
    '& .PlanCard-categoryIcon': {
      fontSize: '25px',
      flexShrink: 0,
      marginTop: '-4px',
      color: tokens.accentCyanBright,
    },
    // Plan-card highlight-icon colour lives here so the card components stay
    // structure-only (no theme.novus reads). Applied via stable child classNames.
    // PlanCard-title, PlanCard-price, and PlanCard-discountTag colours are NOT set here
    // — all three are Typography instances, and a plain ancestor->descendant CSS rule
    // loses to MuiTypography's `defaultProps.color` (confirmed by rendering each and
    // reading getComputedStyle — the rule never surfaced, regardless of what colour was
    // written here). See theme.ts's giant MuiTypography styleOverrides.root: colours for
    // these three are re-homed via `&&` there instead, which actually out-specifies the
    // colour PROP.
    '& .PlanCard-highlightIcon': {
      color: tokens.highlightIconColor,
      fontSize: '20px',
      flexShrink: 0,
    },
    '& .PlanCard-highlights ul': { listStyleType: 'none', margin: 0, padding: 0 },
    '& .PlanCard-detailsLink': {
      background: 'none',
      border: 'none',
      padding: 0,
      fontFamily: 'Roboto',
      fontSize: '14px',
      fontWeight: 600,
      color: tokens.linkColor,
      lineHeight: '150%',
      cursor: 'pointer',
      textAlign: 'left' as const,
      textDecoration: 'none',
      '&:hover': { color: tokens.linkHoverColor },
    },
  };

  const planCardSelectedBorder = `2px solid ${tokens.primaryBrandBlue}`;

  // Per-category selected border / badge background (CPQ Branding -> Plan Card).
  // PlanCard tags its root and badge with `PlanCard--<CategoryType>`, so each category
  // can be targeted without the card component reading the theme itself. Each entry
  // falls back to the brand-derived default, so an unthemed card is unchanged.
  const planCardSelectedByCategory = {
    '&.PlanCard--Internet': { border: asBorder(tokens.planCardBorderSelectedInternet, planCardSelectedBorder) },
    '&.PlanCard--Video': { border: asBorder(tokens.planCardBorderSelectedVideo, planCardSelectedBorder) },
    '&.PlanCard--Phone': { border: asBorder(tokens.planCardBorderSelectedPhone, planCardSelectedBorder) },
  };

  return {
  MuiIconButton: {
    styleOverrides: {
      root: {
        // The DialogHeader close button reads borderBlack, replacing the old instance
        // `sx={{ color }}`. Set on the IconButton's own root (where the original sx
        // applied) so it beats MUI's default action.active colour without a cross-
        // component cascade contest. Scoped to the stable DialogHeader-closeButton
        // class so no other IconButton is affected.
        '&.DialogHeader-closeButton': {
          color: tokens.borderBlack,
        },
      },
    },
  },

  // Shared step-header chrome icons. De-dupes the per-instance `sx={{ fontSize: 20,
  // color }}` that the dialog close button (CloseIcon) and the error-banner mark
  // (ErrorOutlineIcon) repeated across the three configuration step files. Authored
  // as top-level variants under styleOverrides.root (same shape as the MuiButton /
  // MuiPaper variants) so each is opt-in via `variant=` and adds nothing to the
  // markup. Colours reference the flat novusTokens object — theme.* is not in scope
  // inside a variants[] style (slice-21b decision, see DiscountBadge #6) — so the
  // swap stays byte-identical regardless of the literal token values. fontSize is
  // the numeric literal 20 the inline sx used (MUI renders it as 20px).
  MuiSvgIcon: {
    styleOverrides: {
      root: {
        // LeadCaptureConfirmation decorative confirmation-mail icon, replacing the
        // old instance `sx={{ fontSize: '150px' }}`. className seam (sibling of
        // variants) so it stays out of the custom-variant system. Scoped to the
        // stable lead-capture-confirmation-icon class.
        '&.lead-capture-confirmation-icon': { fontSize: '150px' },
        variants: [
          {
            props: { variant: 'dialogCloseIcon' as string },
            style: {
              fontSize: 20,
              color: tokens.borderBlack,
            },
          },
          {
            props: { variant: 'errorBannerIcon' as string },
            style: {
              fontSize: 20,
              color: tokens.errorIcon,
            },
          },
          {
            props: { variant: 'planDetailsCategory' as string },
            style: {
              fontSize: '25px',
              flexShrink: 0,
              marginTop: '-4px',
              color: tokens.accentCyanBright,
            },
          },
          {
            props: { variant: 'planDetailsHighlight' as string },
            style: {
              color: tokens.highlightIconColor,
              fontSize: '20px',
              flexShrink: 0,
              marginTop: '1px',
            },
          },
          {
            props: { variant: 'emptyCartIcon' as string },
            style: {
              fontSize: '116px',
              color: tokens.borderBlack,
            },
          },
          {
            props: { variant: 'readOnlyInfoIcon' as string },
            style: {
              fontSize: '16px',
              color: tokens.quantityLabelColor,
            },
          },
          {
            props: { variant: 'infoDialogCloseIcon' as string },
            style: {
              color: tokens.borderBlack,
            },
          },
        ],
      },
    },
  },

  // Add-on tab strip (TVAddOnsStep). De-dupes the StyledTabs / StyledTab
  // @emotion/styled pair that carried the tab strip's negative-bleed bands,
  // bottom rule, indicator bar, and per-tab typography. Authored as opt-in
  // variants under styleOverrides.root (same shape as the MuiSvgIcon variants
  // above) reading flat novusTokens — theme.* is not in scope inside a
  // variants[] style (slice-21b decision) — so the swap stays byte-identical.
  MuiTabs: {
    styleOverrides: {
      root: {
        variants: [
          {
            props: { variant: 'addOnTabs' as string },
            style: {
              marginLeft: '-30px',
              marginRight: '-30px',
              paddingLeft: '30px',
              paddingRight: '30px',
              borderBottom: `1.5px solid ${tokens.headerBorder}`,
              '& .MuiTabs-indicator': {
                height: 4,
                backgroundColor: tokens.borderBlack,
                borderRadius: '4px',
              },
            },
          },
        ],
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        variants: [
          {
            props: { variant: 'addOnTab' as string },
            style: {
              fontFamily: 'Roboto',
              fontSize: 16,
              fontWeight: 400,
              lineHeight: '100%',
              textTransform: 'none' as const,
              color: tokens.borderBlack,
              '&.Mui-selected': {
                fontWeight: 700,
                color: tokens.borderBlack,
              },
            },
          },
        ],
      },
    },
  },

  MuiPaper: {
    styleOverrides: {
      root: {
        variants: [
          {
            props: { variant: 'dialogHeader' as string },
            style: {
              ...dialogHeaderBase,
              backgroundColor: tokens.accentCyanBright,
            },
          },
          {
            props: { variant: 'dialogHeaderNoBg' as string },
            style: {
              ...dialogHeaderBase,
              backgroundColor: 'transparent',
            },
          },
          {
            props: { variant: 'sideModalHeader' as string },
            style: {
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              height: 98,
              padding: 'clamp(20px, 5vw, 30px)',
              backgroundColor: tokens.accentCyanBright,
              borderBottom: `2px solid ${tokens.bgCyanLight}`,
              boxSizing: 'border-box',
              // Paper ships a default elevation shadow + radius + background-image
              // overlay that the bare Box it replaces never had; null them so the
              // migration stays appearance-neutral.
              borderRadius: 0,
              boxShadow: 'none',
              backgroundImage: 'none',
              // The close icon (an SvgIcon, colour = currentColor) does surface from
              // this Paper descendant rule. The title colour does NOT — the default
              // Typography colour prop wins over an ancestor→descendant rule on the
              // child's computed style — so it is re-homed onto the instance via the
              // MuiTypography `&&` block (SideModalHeader-title class) in theme.ts,
              // matching the DialogHeader-title precedent.
              '& .SideModalHeader-closeIcon': {
                color: tokens.borderBlack,
              },
            },
          },
          {
            // Outer page surface for the Product Offers landing page. Full-bleed by
            // design: the hero and the alternating plan-section bands run edge-to-edge
            // with NO width cap and NO side gutters at any width. The surface carries
            // only the FAB bottom-clearance so the floating contact button never
            // overlaps the last section. Paper ships a default elevation shadow +
            // radius + background.paper fill + background-image overlay that the bare
            // Box it replaces never had; null them all so the migration stays
            // appearance-neutral. paddingBottom binds to the shared pageFabClearance
            // const (the same primitive feeding mixins.page.fabClearance), never a
            // divorced literal.
            props: { variant: 'pageFullBleed' as string },
            style: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              borderRadius: 0,
              backgroundImage: 'none',
              paddingBottom: pageFabClearance,
            },
          },
          {
            // Full-bleed sticky cart-footer bar. Replaces the bare FooterBar Box, so
            // Paper's default elevation shadow + radius + background-image overlay are
            // nulled to keep the swap appearance-neutral. Grows with the stacked mobile
            // layout via min-height; the fixed novusFooterHeight applies only from the
            // Tablet band up. Padding shares the page gutter so the centred Container
            // content lines up with the nav, hero and plan sections at every width.
            // Responsive rules use novusBreakpoints-derived @media keys (object xs/md
            // breakpoint syntax does NOT resolve inside a theme style object).
            props: { variant: 'cartFooterBar' as string },
            style: {
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              minHeight: tokens.novusFooterHeight,
              paddingBlock: '12px',
              paddingInline: pageGutter.xs,
              backgroundColor: tokens.sectionBgWhite,
              boxSizing: 'border-box',
              borderRadius: 0,
              boxShadow: 'none',
              backgroundImage: 'none',
              [tabletUp]: {
                height: tokens.novusFooterHeight,
                paddingBlock: '20px',
                paddingInline: pageGutter.md,
              },
              [desktopUp]: { paddingInline: pageGutter.lg },
              [wideUp]: { paddingInline: pageGutter.xl },
            },
          },
          {
            // Full-bleed alternating coloured band wrapping a PlansSection. Replaces the
            // bare data-coloured Box, so Paper's default elevation shadow + radius +
            // background-image overlay + paper fill are all nulled to keep the swap
            // appearance-neutral; backgroundColor is left transparent so the consumer's
            // data-driven `sx={{ backgroundColor }}` is the band's only fill. Vertical
            // padding is a uniform 40px at every width (40 + 40 = an 80px gap between
            // adjacent categories), so every category has equal breathing room top and
            // bottom — the first band is intentionally NOT special, it uses the same 40px
            // top as the rest. On mobile (tabletDown) the last band keeps an extra 100px
            // bottom cushion via the `--last` className seam the ProductOffers list
            // supplies, for clearance above the sticky cart footer. The shared page gutter
            // keeps the centred Container content aligned with the nav, hero and footer at
            // every width. Responsive rules use novusBreakpoints-derived @media keys (object
            // xs/md breakpoint syntax does NOT resolve inside a theme style object — see
            // slice-21b #2).
            props: { variant: 'planSectionBand' as string },
            style: {
              boxShadow: 'none',
              borderRadius: 0,
              backgroundImage: 'none',
              backgroundColor: 'transparent',
              paddingBlock: '40px',
              paddingInline: pageGutter.xs,
              [tabletDown]: {
                paddingBlockStart: '40px',
                paddingBlockEnd: '40px',
                paddingInline: pageGutter.md,
                '&.plan-section-band--last': { paddingBlockEnd: '100px' },
              },
              [desktopUp]: { paddingInline: pageGutter.lg },
              [wideUp]: { paddingInline: pageGutter.xl },
            },
          },
          {
            // Full-bleed hero band on the Product Offers landing page (AddressHeader).
            // Replaces the bare cyan Box, so Paper's default elevation shadow + radius
            // + background-image overlay are nulled to keep the swap appearance-neutral.
            // Centres its content; padding shares the page gutter so the hero lines up
            // with the nav, plan sections and footer at every width. minHeight is `auto`
            // below the Tablet band so the stacked image -> heading -> address column
            // can grow freely (no 476px mobile clip), then locks to the fixed 476px from
            // the Tablet band up so the desktop two-column hero is byte-identical to the
            // pre-migration Box. Vertical padding mirrors the old py={{ xs:40, lg:0 }}
            // exactly: 40px from Mobile through Tablet, 0 only from the Desktop band up.
            // Responsive rules use novusBreakpoints-derived @media keys (object xs/md
            // breakpoint syntax does NOT resolve inside a theme style object).
            props: { variant: 'heroBanner' as string },
            style: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 'auto',
              backgroundColor: tokens.addressHeaderBg,
              boxSizing: 'border-box',
              paddingBlock: '40px',
              paddingInline: pageGutter.xs,
              borderRadius: 0,
              boxShadow: 'none',
              backgroundImage: 'none',
              // Compact the hero's vertical padding on mobile so the first plan title
              // clears the sticky cart footer in the initial viewport (SC-12451).
              [tabletDown]: {
                paddingBlock: '16px',
              },
              [tabletUp]: {
                minHeight: '476px',
                paddingInline: pageGutter.md,
              },
              [desktopUp]: {
                paddingBlock: 0,
                paddingInline: pageGutter.lg,
              },
              [wideUp]: { paddingInline: pageGutter.xl },
              // Translucent white address card sitting on the cyan band. A descendant
              // background-colour rule surfaces fine on the child's computed style
              // (only the Typography `color` PROP needs the `&&` re-home), so the card
              // fill lives here keyed by the stable AddressHeader-card class instead of
              // an instance `backgroundColor` sx.
              '& .AddressHeader-card': {
                backgroundColor: tokens.addressCardBg,
              },
              // The "Great News!" highlight: a navy heading word with a purple swipe
              // behind it. The pseudo-element + its colour are non-`color`-prop CSS, so
              // they surface as a descendant rule (unlike the heading text colour, which
              // is re-homed onto MuiTypography via `&&` in theme.ts). Geometry matches
              // the pre-migration inline sx exactly.
              '& .AddressHeader-highlight': {
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '1px',
                  // Flush with the word's left edge so "Great News!" lines up with the
                  // heading, address card, and CHANGE ADDRESS button below it; the swipe
                  // still extends past the text on the right for the marker look.
                  left: 0,
                  right: '-12px',
                  height: '13px',
                  backgroundColor: tokens.btnPurpleBg,
                  zIndex: -1,
                },
              },
              // Hero image sizing is theme-owned (moved off the component's inline
              // style, which was a standing SC-9731 magic-px blocker). The image stays
              // visible at every width (per customer feedback, reversing the earlier
              // SC-12535 phone hide). On desktop it fills the side column at full size;
              // on the stacked bands the column is full-width, so bound it to a fraction
              // of that column — the image then scales fluidly with the viewport like the
              // desktop image (width:100%/height:auto), just smaller, no letterboxing.
              '& .AddressHeader-heroImage img': {
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                maxWidth: '576px',
                maxHeight: '408px',
                [belowDesktop]: { maxWidth: '55%' },
              },
            },
          },
          {
            // Full-bleed sticky top nav bar (TopNavBar). Replaces the bare brand-blue
            // `Box component="header"`, so Paper's default elevation shadow + radius +
            // background-image overlay are nulled to keep the swap appearance-neutral.
            // Stays sticky at the top of the viewport (top:0, zIndex 1100, full width).
            // Padding shares the page gutter so the nav's centred Container content lines
            // up with the hero, plan sections and footer at every width. Responsive rules
            // use novusBreakpoints-derived @media keys (object xs/md breakpoint syntax
            // does NOT resolve inside a theme style object — see slice-21b decision #2).
            props: { variant: 'navTopBar' as string },
            style: {
              backgroundColor: tokens.primaryBrandBlue,
              width: '100%',
              position: 'sticky',
              top: 0,
              zIndex: 1100,
              boxSizing: 'border-box',
              borderRadius: 0,
              boxShadow: 'none',
              backgroundImage: 'none',
              paddingInline: pageGutter.xs,
              [tabletUp]: { paddingInline: pageGutter.md },
              [desktopUp]: { paddingInline: pageGutter.lg },
              [wideUp]: { paddingInline: pageGutter.xl },
              // The 67px bar height lives on the inner Stack row (keyed by the stable
              // NavTopBar-row class) — not on this band — so the R7 page-cap Container
              // still measures full width while the row keeps its fixed height. The row's
              // direction/space-between/centre come from the Stack's own layout props (a
              // Stack writes flex-direction onto itself at a specificity an ancestor→
              // descendant rule can't beat — the same reason the count colour is re-homed
              // below). The cart count colour (cyanAccent) is re-homed onto the count
              // Typography via the MuiTypography `&&` block (NavTopBar-cartCount class) in
              // theme.ts: a Typography `color` PROP wins over an ancestor→descendant rule
              // on the child's computed style. Same precedent as AddressHeader-heading.
              '& .NavTopBar-row': {
                height: '67px',
              },
              // Brand logo glyph: the cyan fill + 130px scale that the component's inline
              // SvgIcon sx previously held. An SvgIcon paints from `color` (currentColor),
              // so this descendant rule surfaces on its computed style — no `&&` re-home
              // needed (unlike the count Typography, whose colour PROP wins). Keyed by the
              // stable NavTopBar-logo class so no other icon is touched.
              '& .NavTopBar-logo': {
                fontSize: '130px',
                color: tokens.addressHeaderBg,
              },
              // Tenant <img> logo path: the SvgIcon fallback above letterboxes inside
              // inheritViewBox at 130px with no overflow risk, but a wordmark image has
              // no such slack — cap both dimensions explicitly rather than deriving width
              // from height alone (maxWidth:'100%' on the img is meaningless inside the
              // shrink-wrapping IconButton it sits in).
              // `img.NavTopBar-logo`, NOT `.NavTopBar-logo img` — CustomerLogo applies this
              // className directly onto the rendered <img>/<svg> itself (TopNavBar.tsx),
              // there is no wrapping element carrying the class with the img nested inside
              // it. A descendant selector here matches nothing; the compound tag+class
              // selector is required, and scopes this cap to the <img> branch only so the
              // SvgIcon fallback (an <svg>, unaffected by this selector) keeps its 130px.
              '& img.NavTopBar-logo': {
                maxHeight: tokens.navLogoImgMaxHeight,
                maxWidth: tokens.navLogoImgMaxWidth,
              },
            },
          },
          {
            // Full-bleed sticky checkout header (CheckoutHeader). Replaces the bare
            // brand-blue `Box component="header"`, so Paper's default elevation shadow +
            // radius + background-image overlay are nulled to keep the swap appearance-
            // neutral (the old Box had none). Stays sticky at the top of the viewport
            // (top:0, zIndex 1100, full width). Responsive rules use a novusBreakpoints-
            // derived @media key (object xs/sm breakpoint syntax does NOT resolve inside a
            // theme style object — see slice-21b decision #2). Two DISTINCT cyans: the
            // logo glyph reads addressHeaderBg (#00CEFF), the cart icon reads
            // accentCyanBright (#00CDFF) — do not collapse them. The row's fixed 67px
            // height + responsive gutter live on the inner Stack row (keyed by the stable
            // CheckoutHeader-row class), and the logo's currentColor fill + responsive
            // scale on the CheckoutHeader-logo class — an SvgIcon paints from `color`, so
            // a descendant rule surfaces on its computed style with no `&&` re-home. The
            // title + cart-label Typography colours, which take a `color` PROP, are re-homed
            // onto MuiTypography via `&&` in theme.ts. Additive: opt-in via
            // variant="checkoutHeader".
            props: { variant: 'checkoutHeader' as string },
            style: {
              backgroundColor: tokens.primaryBrandBlue,
              width: '100%',
              position: 'sticky',
              top: 0,
              zIndex: 1100,
              boxSizing: 'border-box',
              borderRadius: 0,
              boxShadow: 'none',
              backgroundImage: 'none',
              '& .CheckoutHeader-row': {
                height: '67px',
                paddingInline: '16px',
                [`@media (min-width:${novusBreakpoints.sm}px)`]: { paddingInline: '36px' },
              },
              '& .CheckoutHeader-logo': {
                fontSize: '90px',
                color: tokens.addressHeaderBg,
                [`@media (min-width:${novusBreakpoints.sm}px)`]: { fontSize: '130px' },
              },
              // Tenant <img> logo path, mirroring the SvgIcon fallback's own 90px->130px
              // sm-breakpoint scale above instead of a single flat cap for every width.
              // `img.CheckoutHeader-logo`, NOT `.CheckoutHeader-logo img` — same reason as
              // NavTopBar-logo above: CustomerLogo applies this className directly onto the
              // rendered <img>/<svg> (CheckoutHeader.tsx), there is no wrapper to descend
              // into. The compound tag+class selector scopes this to the <img> branch only.
              '& img.CheckoutHeader-logo': {
                maxHeight: tokens.checkoutLogoImgMaxHeightMobile,
                maxWidth: tokens.checkoutLogoImgMaxWidthMobile,
                [`@media (min-width:${novusBreakpoints.sm}px)`]: {
                  maxHeight: tokens.checkoutLogoImgMaxHeight,
                  maxWidth: tokens.checkoutLogoImgMaxWidth,
                },
              },
              '& .CheckoutHeader-cartIcon': {
                color: tokens.accentCyanBright,
              },
            },
          },
          {
            // Add-on "discount" pill (DiscountBadge). Replaces the bare styled Box, so
            // Paper's default elevation shadow + radius + background.paper fill + text
            // colour + background-image overlay are all nulled to keep the swap
            // appearance-neutral: the old Box had no shadow, no radius, no own text
            // colour (it inherited from its parent), and a cyan fill. color:'inherit'
            // restores that inherited label colour; borderRadius:0 / boxShadow:'none' /
            // backgroundImage:'none' strip Paper's defaults. Geometry (inline-flex,
            // centred, flex-start self-align, fixed height, padding) matches the old
            // Box token-for-token. Additive: opt-in via variant="discountBadge".
            props: { variant: 'discountBadge' as string },
            style: {
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'flex-start',
              // Was tokens.addOnDiscountBadgeBg (hardcoded '#00cdff' cyan, since removed —
              // it had no other consumer). discountBadgeBg is the one actually wired from
              // tenant config (discount.bgDiscount), so a tenant's configured discount-badge
              // colour now reaches this, the component's only real consumer.
              backgroundColor: tokens.discountBadgeBg,
              padding: tokens.addOnDiscountBadgePadding,
              [tabletUp]: { height: tokens.addOnDiscountBadgeHeight },
              color: 'inherit',
              boxShadow: 'none',
              borderRadius: 0,
              backgroundImage: 'none',
            },
          },
          {
            // Cart-item card surface (CartHardwareContent equipment rows; reused by
            // the CartAddOnsContent surfaces). Replaces the bare inline-sx Box, so
            // Paper's default elevation shadow + background-image overlay are nulled
            // to keep the swap appearance-neutral — the old Box had none. This is the
            // TRULY-SHARED surface: a 1.5px solid headerBorder border + 10px radius +
            // white fill. Per-instance padding (15px in CartHardwareContent) is NOT
            // part of the variant — it stays on the consumer. Additive: opt-in via
            // variant="cartItemSurface".
            props: { variant: 'cartItemSurface' as string },
            style: {
              backgroundColor: symphonaTheme.colors.white,
              border: `1.5px solid ${tokens.headerBorder}`,
              borderRadius: '10px',
              boxShadow: 'none',
              backgroundImage: 'none',
            },
          },
          {
            // Sticky cart-drawer footer surface (CartDrawerFooter). Replaces the
            // DrawerFooter styled(Box); Paper's default elevation shadow / rounded
            // radius / background-image overlay are nulled so the swap is
            // appearance-neutral — the old Box had none, only its explicit
            // 1px 1px 10px 2px shadow. Additive: opt-in via variant="cartDrawerFooter".
            props: { variant: 'cartDrawerFooter' as string },
            style: {
              position: 'sticky',
              bottom: 0,
              zIndex: 10,
              // CPQ Branding -> Cart Drawer -> "Footer Background" / "Footer Text".
              // Both were previously inert: this variant hardcoded white and nothing
              // read novus.cartFooterBg/cartFooterColor outside two palette aliases.
              backgroundColor: tokens.cartFooterSurfaceBg || symphonaTheme.colors.white,
              ...(tokens.cartFooterTextColor ? { color: tokens.cartFooterTextColor } : {}),
              padding: 'clamp(20px, 5vw, 30px)',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              boxShadow: tokens.cartFooterShadow,
              borderRadius: 0,
              backgroundImage: 'none',
              // Landscape / short-viewport: compact the footer's own vertical
              // padding and inter-row gap so the drawer's scrollable item list
              // keeps a usable height instead of collapsing to a sliver. Mirrors
              // the configurePlanDialog shortViewportDown treatment; additive and
              // height-gated (>520px tall stays byte-identical on desktop/portrait).
              [shortViewportDown]: {
                paddingBlock: novusTokens.cartDrawerFooterShortViewportPaddingBlock,
                gap: novusTokens.cartDrawerFooterShortViewportGap,
              },
            },
          },
          {
            // Service Address card (CustomerInfoStep). Replaces the bare inline-sx Box
            // that laid out the address column against the Edit link. Paper's default
            // elevation shadow + background.paper fill + background-image overlay are
            // nulled to keep the swap appearance-neutral — the old Box had only a 1px
            // grey outline, 10px radius and 16/20 padding. The outline reads the
            // serviceCardBorder token so a tenant theme can re-source it. Additive:
            // opt-in via variant="serviceAddressCard".
            props: { variant: 'serviceAddressCard' as string },
            style: {
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              border: `1px solid ${tokens.serviceCardBorder}`,
              borderRadius: '10px',
              padding: '16px 20px',
              backgroundColor: 'transparent',
              boxShadow: 'none',
              backgroundImage: 'none',
            },
          },
          {
            // Technician-visit note box (InstallationStep / TechnicianSection). Replaces
            // the bare inline-sx Box that framed the TimeScheduler with a 1px grey outline,
            // 15px radius and 16px padding. Paper's default elevation shadow + background.paper
            // fill + background-image overlay are nulled to keep the swap appearance-neutral.
            // Reuses the serviceCardBorder token coined for the service-address card so a
            // tenant theme re-sources both outlines at once. Distinct from serviceAddressCard
            // only by radius (15 vs 10), padding (16 vs 16/20) and the absence of flex layout.
            // Additive: opt-in via variant="technicianNoteBox".
            props: { variant: 'technicianNoteBox' as string },
            style: {
              border: `1px solid ${tokens.serviceCardBorder}`,
              borderRadius: '15px',
              padding: '16px',
              backgroundColor: 'transparent',
              boxShadow: 'none',
              backgroundImage: 'none',
            },
          },
          {
            // File-preview card (PhoneNumberStep). Replaces the bare inline-sx Stack that
            // framed an uploaded file's thumbnail + name/date column with a 1px channel-logo
            // outline, 12px radius, a REAL white fill (sectionBgWhite — not transparent) and
            // overflow:hidden. The outer Stack was a default column, so display/flexDirection
            // are folded in to reproduce the layout byte-identically. The nested
            // `.FilePreviewCard-thumbnail` selector homes the 120px off-white thumbnail band
            // and its centering so the component carries no raw hex. Paper's default elevation
            // shadow + background-image overlay are nulled. The conditional position (prop-driven
            // when a remove control is present) stays as a minimal residual sx on the consumer —
            // it is neither hex nor rem. Additive: opt-in via variant="filePreviewCard".
            props: { variant: 'filePreviewCard' as string },
            style: {
              display: 'flex',
              flexDirection: 'column',
              width: '180px',
              border: `1px solid ${tokens.channelLogoBorder}`,
              borderRadius: '12px',
              overflow: 'hidden',
              backgroundColor: tokens.sectionBgWhite,
              boxShadow: 'none',
              backgroundImage: 'none',
              '& .FilePreviewCard-thumbnail': {
                height: '120px',
                backgroundColor: tokens.fileThumbnailBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              },
            },
          },
          {
            // Applied-coupon box (CouponSection). Replaces the bare styled Stack that framed
            // the applied code + Remove control with a 2px green outline, 10px radius and
            // 2/5/2/14 padding. The outline + inert text colour read couponAppliedAccent so a
            // tenant theme can re-source the green; the colour/fontWeight are inert here
            // (children override them) but preserved for byte-fidelity with the old Stack.
            // Paper's default elevation shadow + background.paper fill + background-image
            // overlay are nulled to keep the swap appearance-neutral — the old Stack had no
            // fill. Additive: opt-in via variant="couponApplied".
            props: { variant: 'couponApplied' as string },
            style: {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '2px 5px 2px 14px',
              borderRadius: '10px',
              border: `2px solid ${tokens.couponAppliedAccent}`,
              color: tokens.couponAppliedAccent,
              fontWeight: 600,
              backgroundColor: 'transparent',
              boxShadow: 'none',
              backgroundImage: 'none',
            },
          },
          {
            // Outer panel framing the Checkout OrderSummary. Replaces the bare Box that
            // wrapped the whole summary with a cyan 1px outline, 10px radius and a REAL
            // white fill (sectionBgWhite — NOT transparent, unlike the band/coupon
            // variants which let the consumer paint). overflow:hidden clips the inner
            // section dividers to the rounded corners. Paper's default elevation shadow +
            // background-image overlay are nulled to keep the swap appearance-neutral.
            // Additive: opt-in via variant="orderSummaryPanel".
            props: { variant: 'orderSummaryPanel' as string },
            style: {
              backgroundColor: tokens.sectionBgWhite,
              border: `1px solid ${tokens.accentCyan}`,
              borderRadius: '10px',
              overflow: 'hidden',
              boxShadow: 'none',
              backgroundImage: 'none',
            },
          },
          {
            // Outer page surface for the Order Confirmation page. Full-height cyan
            // wash replacing the bare `Box sx={{ minHeight:'100vh', backgroundColor }}`.
            // Paper's default elevation shadow + radius + background-image overlay are
            // nulled to keep the swap appearance-neutral — the old Box had none. Additive:
            // opt-in via variant="orderConfirmationPage".
            props: { variant: 'orderConfirmationPage' as string },
            style: {
              minHeight: '100vh',
              backgroundColor: tokens.orderConfirmationBg,
              boxShadow: 'none',
              backgroundImage: 'none',
            },
          },
          {
            // Order Confirmation page header. Replaces the bare brand-blue
            // `Box component="header"`, so Paper's default elevation shadow + radius +
            // background-image overlay are nulled to keep the swap appearance-neutral.
            // CRITICALLY NOT sticky — unlike checkoutHeader / navTopBar this header has
            // no position/top/zIndex, so it scrolls with the page (the load-bearing
            // divergence from those bars; do not copy their sticky keys). The 67px row
            // height + 36px gutter live on the inner Stack row (keyed by the stable
            // OrderConfirmationHeader-row class); the logo's cyan currentColor fill +
            // 130px scale on the OrderConfirmationHeader-logo class — an SvgIcon paints
            // from `color`, so a descendant rule surfaces on its computed style with no
            // `&&` re-home. Additive: opt-in via variant="orderConfirmationHeader".
            props: { variant: 'orderConfirmationHeader' as string },
            style: {
              backgroundColor: tokens.primaryBrandBlue,
              width: '100%',
              borderRadius: 0,
              boxShadow: 'none',
              backgroundImage: 'none',
              '& .OrderConfirmationHeader-row': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '67px',
                paddingInline: '36px',
              },
              '& .OrderConfirmationHeader-logo': {
                fontSize: '130px',
                color: tokens.addressHeaderBg,
              },
            },
          },
          {
            // Order Confirmation card. Replaces the bare white inline-sx Box that framed
            // the thank-you content with a 1.5px cyan outline, 20px radius and centred
            // column layout. Paper's default elevation shadow + background-image overlay
            // are nulled to keep the swap appearance-neutral. Per-instance responsive
            // padding stays on the consumer (deferred). The confirmation glyph's 160px
            // size lives on the OrderConfirmationCard-icon descendant. Additive: opt-in
            // via variant="orderConfirmationCard".
            props: { variant: 'orderConfirmationCard' as string },
            style: {
              backgroundColor: tokens.sectionBgWhite,
              borderRadius: '20px',
              border: `1.5px solid ${tokens.orderConfirmationCardBorder}`,
              maxWidth: '800px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: '24px',
              boxShadow: 'none',
              backgroundImage: 'none',
              '& .OrderConfirmationCard-icon': {
                width: '160px',
                height: '160px',
                fontSize: '160px',
              },
            },
          },
          {
            // Checkout step-accordion card surface (CheckoutStepRenderer). Replaces the
            // bare StepCard styled(Box) that framed each step with the token-sourced card
            // shadow, a 10px radius, hidden overflow and a white fill. Paper's default
            // elevation shadow is supplanted by the explicit stepCardShadow token and its
            // background-image overlay is nulled, so the swap stays appearance-neutral.
            // Additive: opt-in via variant="checkoutStepCard".
            props: { variant: 'checkoutStepCard' as string },
            style: {
              boxShadow: tokens.stepCardShadow,
              borderRadius: '10px',
              overflow: 'hidden',
              backgroundColor: symphonaTheme.colors.white,
              backgroundImage: 'none',
            },
          },
          {
            // Checkout step-accordion header band (CheckoutStepRenderer). Replaces the bare
            // StepHeader styled(Box) that laid out the step label/title against the Edit
            // control: a flex space-between row with 24/30 padding, a lightGray2 bottom rule
            // and a white fill. Paper's default elevation shadow + radius + background-image
            // overlay are nulled to keep the swap appearance-neutral. The legacy
            // max-width:600px padding step is preserved verbatim (a later responsive cycle
            // re-keys it). Additive: opt-in via variant="checkoutStepHeader".
            props: { variant: 'checkoutStepHeader' as string },
            style: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '24px 30px',
              borderBottom: `1px solid ${symphonaTheme.colors.lightGray2}`,
              backgroundColor: symphonaTheme.colors.white,
              boxShadow: 'none',
              borderRadius: 0,
              backgroundImage: 'none',
              [mobileDown]: { padding: '12px 16px' },
            },
          },
          {
            // Checkout step-accordion content well (CheckoutStepRenderer). Replaces the bare
            // StepContent styled(Box) that padded each expanded step body at 24/30 over a
            // transparent fill. Paper's default elevation shadow + radius + paper fill +
            // background-image overlay are nulled to keep the swap appearance-neutral. The
            // mobile padding step is keyed to novusBreakpoints (mobileDown). Additive: opt-in via
            // variant="checkoutStepContent".
            props: { variant: 'checkoutStepContent' as string },
            style: {
              padding: '24px 30px',
              backgroundColor: 'transparent',
              boxShadow: 'none',
              borderRadius: 0,
              backgroundImage: 'none',
              [mobileDown]: { padding: '16px' },
            },
          },
          {
            // Checkout completed-step read-only grid (CustomerInfoStep ViewOnly). Replaces
            // the bare ReadOnlyGrid styled(Box) that laid the saved fields out in a 3-column
            // 36px-gap grid over a transparent fill. Paper's default elevation shadow + radius
            // + paper fill + background-image overlay are nulled to keep the swap appearance-
            // neutral. Both breakpoints are keyed to the novusBreakpoints scale: 2-col below md
            // (768.95px), 1-col + 16px gap below sm (480.95px, mobileDown).
            // Additive: opt-in via variant="checkoutReadOnlyGrid".
            props: { variant: 'checkoutReadOnlyGrid' as string },
            style: {
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '36px',
              backgroundColor: 'transparent',
              boxShadow: 'none',
              borderRadius: 0,
              backgroundImage: 'none',
              [`@media (max-width:${novusBreakpoints.md - 0.05}px)`]: { gridTemplateColumns: 'repeat(2, 1fr)' },
              [mobileDown]: { gridTemplateColumns: '1fr', gap: '16px' },
            },
          },
          {
            // Cyan rounded hero placeholder at the top of the CheckAvailabilityDialog
            // (the StyledImageBanner Box it replaces). Paper ships a default elevation
            // shadow + radius + background.paper fill + background-image overlay that the
            // bare Box never had; null the shadow + overlay so the migration stays
            // appearance-neutral (borderRadius/backgroundColor are explicitly set here).
            // Additive: opt-in via variant="checkAvailabilityImageBanner".
            props: { variant: 'checkAvailabilityImageBanner' as string },
            style: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: tokens.bgCyanLight,
              borderRadius: 15,
              height: 175,
              width: '100%',
              boxSizing: 'border-box',
              flexShrink: 0,
              padding: '30px',
              overflow: 'hidden',
              boxShadow: 'none',
              backgroundImage: 'none',
              // 269px icon size folded in from ImageBanner.tsx's inline SvgIcon sx (MC-4)
              // so the migrated <Paper variant> sizes its decorative icon child itself.
              '& .MuiSvgIcon-root': { fontSize: '269px' },
            },
          },
          {
            // Soft-amber rounded callout in the CheckAvailabilityDialog's
            // change_address_warning view (the Stack `bgcolor={theme.novus.warningBg}
            // borderRadius="10px" p="15px" mt="20px"` it replaces) warning the user a
            // service-address change resets their order. The bare Stack carried no
            // elevation/overlay; Paper ships a default shadow + background-image overlay +
            // background.paper fill, so null the shadow + overlay (the warningBg fill is set
            // explicitly here) to keep the swap appearance-neutral — the
            // checkAvailabilityImageBanner / cartDrawerFooter precedent. The Stack's default
            // flex column is preserved so the single text child positions identically. The
            // inline literals carry over verbatim (string units, not MUI spacing numbers).
            // Additive: opt-in via variant="warningCallout".
            props: { variant: 'warningCallout' as string },
            style: {
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: tokens.warningBg,
              borderRadius: '10px',
              padding: '15px',
              marginTop: '20px',
              boxShadow: 'none',
              backgroundImage: 'none',
            },
          },
        ],
      },
    },
  },

  // Edit-control link for the Service Address card (CustomerInfoStep). De-dupes the
  // per-instance `sx` that the bare MUI Link carried (brand link colour + edit-control
  // typography). Authored as an opt-in variant under styleOverrides.root (same
  // variants[] shape as MuiTab) reading the flat novusTokens object — theme.* is not
  // in scope inside a variants[] style (slice-21b decision) — so the swap stays
  // byte-identical. Additive: opt-in via variant="serviceAddressEdit".
  MuiLink: {
    styleOverrides: {
      root: {
        variants: [
          {
            props: { variant: 'serviceAddressEdit' as string },
            style: {
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              lineHeight: '130%',
              color: tokens.linkColor,
              textDecoration: 'none',
              flexShrink: 0,
              marginLeft: '16px',
            },
          },
          {
            // Learn-more link for the applied-coupon panel (CouponSection). Carries only the
            // removed underline that the bare MUI Link previously set via per-instance
            // `sx={{ textDecoration: 'none' }}`. Deliberately colourless — the purple stays on
            // the nested "Click Here" span via its own Typography className seam, so this
            // variant never paints the link itself. Additive: opt-in via variant="couponLearnMore".
            props: { variant: 'couponLearnMore' as string },
            style: { textDecoration: 'none' },
          },
        ],
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        color: tokens.novusPurpleColor,
        textTransform: 'uppercase' as const,
        fontWeight: 700,
        // Compact 30px button height with tight side padding, so a full action row —
        // e.g. the cart footer's totals + Show Cart + Checkout — stays on one line at
        // narrow widths. Height is pinned rather than derived from vertical padding:
        // the label's 20.8px line-height makes 6px padding read as ~33px, so a fixed
        // height is what lands exactly 30px across every variant and border width.
        minHeight: '30px',
        height: '30px',
        paddingBlock: 0,
        paddingInline: '10px',
        '&.Mui-disabled': {
          opacity: 0.5,
        },
        variants: [
          // Footer "Show Cart" action button. A dedicated variant value (not a
          // custom boolean prop, which would leak to the DOM) so it adds nothing
          // to the markup: `nowrap` holds the label on one line at the compact
          // <md footer width — without it the row's shrinking flex track lets
          // "Show Cart" wrap to two lines — and `none` textTransform keeps the
          // mixed-case label that the root override would otherwise uppercase.
          // The outlined-look borders/hover/disabled are reused from the same
          // novusPurple* tokens as styleOverrides.outlined so the two stay in
          // lockstep. Additive: opt-in via variant="footerAction".
          {
            props: { variant: 'footerAction' },
            style: {
              whiteSpace: 'nowrap',
              textTransform: 'none' as const,
              color: tokens.editPlanColor,
              backgroundColor: tokens.editPlanBg,
              border: tokens.editPlanBorder,
              '&:hover': {
                color: tokens.editPlanHoverColor,
                backgroundColor: tokens.editPlanHoverBg,
                border: tokens.editPlanBorder,
              },
              '&.Mui-disabled': {
                color: tokens.editPlanColor,
                border: tokens.editPlanBorder,
              },
            },
          },
          {
            // CHANGE ADDRESS action in the hero address card. Carries the same purple
            // contained fill + hover as styleOverrides.contained (so the two stay in
            // lockstep) plus a 1.5px brand-purple ring and an intrinsic max-content
            // width — the inline sx the AddressHeader Box previously held. The 700
            // weight + uppercase come from the root override, so they are not repeated.
            // Additive: opt-in via variant="changeAddress".
            props: { variant: 'changeAddress' },
            style: {
              width: 'max-content',
              border: `1.5px solid ${tokens.btnPurpleBg}`,
              backgroundColor: tokens.novusPurpleBg,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: tokens.novusPurpleContainedHoverBg,
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                backgroundColor: tokens.novusPurpleBg,
                color: tokens.novusPurpleColor,
              },
            },
          },
          {
            // "+N More" channel-overflow trigger in TVChannelLogos. A text-style button
            // that drops the root override's uppercase/700/purple chrome for a mixed-case
            // underlined 600-weight black link look. The root forces text-transform
            // uppercase, font-weight 700 and the purple label colour, so this variant must
            // RE-STATE textTransform none, the 600 weight and the black colour to win —
            // otherwise the trigger regresses to an uppercase purple button. Typography
            // matches the prior styled(Button) authored values byte-for-byte ('Roboto' /
            // 600 / 16px / 150%), not the roboto-600-16 variant's RB stack + 24px, so the
            // rendered look is unchanged. Additive: opt-in via variant="channelMore".
            props: { variant: 'channelMore' },
            style: {
              fontFamily: 'Roboto',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '150%',
              textTransform: 'none' as const,
              textDecoration: 'underline',
              minWidth: 'auto',
              color: tokens.borderBlack,
            },
          },
          {
            // Cart action in the top nav bar. A text-style button: cyanAccent label on a
            // transparent surface (the bar's brand-blue shows through), with the standard
            // MUI text-button 6/8 padding and no border. Re-points the root override's
            // novusPurple label colour to cyanAccent and reinstates the translucent-white
            // hover the inline sx previously held. The count digit's cyanAccent colour is
            // NOT set here — a Typography `color` PROP wins over an ancestor→descendant
            // rule on the child's computed style — so it is re-homed onto the count
            // Typography via the MuiTypography `&&` block (NavTopBar-cartCount class) in
            // theme.ts, same precedent as AddressHeader-heading. Additive: opt-in via
            // variant="navCartAction".
            props: { variant: 'navCartAction' },
            style: {
              color: tokens.cyanAccent,
              backgroundColor: 'transparent',
              border: 0,
              padding: '6px 8px',
              '&:hover': {
                backgroundColor: tokens.navBarCartHoverBg,
              },
            },
          },
          {
            // "Remove All Items" action in the shopping-cart drawer. A borderless
            // text-link button in the linkColor token, mirroring the channelMore
            // link-look precedent. The root override forces uppercase / 700 / purple,
            // so this variant RE-STATES textTransform none, the 600 weight, the
            // linkColor and zero padding to win, plus a transparent-underline hover.
            // Typography matches the prior styled(LoadingButton) authored values
            // byte-for-byte. Additive: opt-in via variant="cartRemoveAll".
            props: { variant: 'cartRemoveAll' },
            style: {
              color: tokens.linkColor,
              textTransform: 'none' as const,
              fontWeight: 600,
              fontSize: '16px',
              fontFamily: 'Roboto',
              padding: 0,
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            },
          },
          {
            // Checkout button in the shopping-cart drawer footer. A full-width uppercase
            // purple primary button: primaryBrandBlue label on the purplePrimary surface,
            // square corners, capped at 30px tall. Its hover deliberately uses the
            // dedicated btnPurpleHoverBg token — NOT the contained variant's hover — and
            // its disabled state holds the purplePrimary fill with a literal white label at
            // 0.5 opacity. Mirrors the prior styled(LoadingButton) authored values
            // byte-for-byte. Additive: opt-in via variant="checkoutDrawer".
            props: { variant: 'checkoutDrawer' },
            style: {
              width: '100%',
              color: tokens.primaryBrandBlue,
              backgroundColor: tokens.purplePrimary,
              fontWeight: 700,
              fontSize: '16px',
              textTransform: 'uppercase' as const,
              borderRadius: '0px',
              padding: '9px 10px',
              maxHeight: '30px',
              fontFamily: 'Roboto',
              '&:hover': {
                backgroundColor: tokens.btnPurpleHoverBg,
              },
              '&.Mui-disabled': {
                backgroundColor: tokens.purplePrimary,
                color: tokens.checkoutDrawerDisabledColor,
                opacity: 0.5,
              },
            },
          },
          {
            // DECLINE action in the Terms & Conditions dialog. An outlined button whose
            // label takes the brand navy colour. The
            // outlined ring/hover/disabled chrome is re-stated from the same novusPurple*
            // tokens as styleOverrides.outlined so the two stay in lockstep. Additive:
            // opt-in via variant="termsDecline".
            props: { variant: 'termsDecline' as string },
            style: {
              '&&': { color: tokens.primaryBrandBlue },
              border: tokens.novusPurpleOutlinedBorder,
              '&:hover': {
                backgroundColor: tokens.novusPurpleBg,
                border: tokens.novusPurpleOutlinedBorder,
              },
              '&.Mui-disabled': {
                color: tokens.novusPurpleColor,
                border: tokens.novusPurpleOutlinedBorder,
              },
            },
          },
          {
            // Full-width uppercase purple "Check Availability" submit CTA in the
            // CheckAvailabilityDialog (the StyledCTAButton it replaces). Purple fill +
            // brand-blue label reuse btnPurpleBg / primaryBrandBlue; the hover fill maps
            // to its own btnPurpleCtaHoverBg token (#c480f5), distinct from the outlined
            // novusPurple hover. Additive: opt-in via variant="checkAvailabilityCta".
            props: { variant: 'editPlan' as string },
            style: {
              color: tokens.editPlanColor,
              backgroundColor: tokens.editPlanBg,
              border: tokens.editPlanBorder,
              boxShadow: 'none',
              '&:hover': {
                color: tokens.editPlanHoverColor,
                backgroundColor: tokens.editPlanHoverBg,
                border: tokens.editPlanBorder,
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                color: tokens.editPlanColor,
                border: tokens.editPlanBorder,
              },
            },
          },
          {
            props: { variant: 'checkAvailabilityCta' as string },
            style: {
              backgroundColor: tokens.btnPurpleBg,
              color: tokens.btnTextColor,
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 700,
              fontSize: 16,
              textTransform: 'uppercase',
              width: '100%',
              paddingTop: 9,
              paddingBottom: 9,
              paddingLeft: 10,
              paddingRight: 10,
              '&:hover': {
                backgroundColor: tokens.btnPurpleCtaHoverBg,
              },
              '&.Mui-disabled': {
                backgroundColor: tokens.btnPurpleBg,
                color: tokens.btnTextColor,
                opacity: 0.5,
              },
            },
          },
        ],
      },
      outlined: {
        border: tokens.novusPurpleOutlinedBorder,
        '&:hover': {
          backgroundColor: tokens.novusPurpleBg,
          border: tokens.novusPurpleOutlinedBorder,
        },
        '&.Mui-disabled': {
          color: tokens.novusPurpleColor,
          border: tokens.novusPurpleOutlinedBorder,
        },
      },
      contained: {
        backgroundColor: tokens.novusPurpleBg,
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: tokens.novusPurpleContainedHoverBg,
          boxShadow: 'none',
        },
        '&.Mui-disabled': {
          backgroundColor: tokens.novusPurpleBg,
          color: tokens.novusPurpleColor,
        },
      },
    },
  },

  MuiCheckbox: {
    styleOverrides: {
      root: {
        padding: 0,
        width: tokens.addOnCheckboxSize,
        height: tokens.addOnCheckboxSize,
        borderRadius: '2px',
        flexShrink: 0,
        marginTop: '2px',
        alignSelf: 'flex-start',
        color: tokens.borderBlack,
        '&.Mui-checked': {
          color: tokens.checkboxColor,
        },
        '&.Mui-disabled': {
          color: tokens.borderDisabled,
        },
        '& .MuiSvgIcon-root': {
          fontSize: 20,
        },
      },
    },
  },

  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        marginLeft: 0,
        '&:last-child': {
          marginRight: 0,
        },
      },
      label: {
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '21px',
        color: symphonaTheme.colors.primary,
        fontFamily: 'Roboto',
        marginLeft: '7px',
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        variants: [
          {
            props: { variant: 'selectedTag' as string },
            style: {
              backgroundColor: tokens.sectionBgWhite,
              color: tokens.borderBlack,
              '&.MuiAutocomplete-tag': {
                backgroundColor: tokens.sectionBgWhite,
                color: tokens.borderBlack,
              },
              border: `1px solid ${tokens.accentCyan}`,
              borderRadius: '6px',
              fontFamily: MS,
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '21px',
              height: 'auto',
              '& .MuiChip-label': { padding: '4px 8px' },
              '& .MuiChip-deleteIcon': {
                color: tokens.primaryBrandBlue,
                '&:hover': { color: tokens.headingBlue },
              },
            },
          },
          {
            props: { variant: 'planSelectedBadge' as string },
            style: {
              flexShrink: 0,
              backgroundColor: tokens.primaryBrandBlue,
              '&.PlanCard--Internet': { backgroundColor: tokens.planCardBadgeBgInternet || tokens.primaryBrandBlue },
              '&.PlanCard--Video': { backgroundColor: tokens.planCardBadgeBgVideo || tokens.primaryBrandBlue },
              '&.PlanCard--Phone': { backgroundColor: tokens.planCardBadgeBgPhone || tokens.primaryBrandBlue },
              color: tokens.sectionBgWhite,
              fontWeight: 600,
              fontSize: '14px',
              height: 'auto',
              '& .MuiChip-label': { padding: '9px 17px' },
            },
          },
        ],
      },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        variants: [
          {
            props: { variant: 'planCard' as string },
            style: {
              ...planCardBase,
              '& > .PlanCard-highlights': { marginTop: 'auto' },
            },
          },
          {
            props: { variant: 'planCard' as string, selected: true },
            style: {
              border: planCardSelectedBorder,
              ...planCardSelectedByCategory,
            },
          },
          {
            props: { variant: 'planCardSingle' as string },
            style: {
              ...planCardBase,
              '& > .PlanCard-highlights': { justifyContent: 'space-between' },
              // Stacked (mobile/tablet-portrait) layout: the price shows under the
              // title in the info column; the pricing-column copy is hidden so the
              // price isn't pushed below the highlights.
              '& .PlanCard-pricingPrice': { display: 'none' },
              [tabletUp]: {
                flexDirection: 'row',
                gap: '30px',
                '& > .PlanCard-info': { flex: 1.2 },
                '& > .PlanCard-highlights': { flex: 1.5 },
                '& > .PlanCard-pricing': { flex: 1 },
                // Row layout: the price belongs in the right-hand pricing column;
                // hide the info-column copy.
                '& .PlanCard-infoPrice': { display: 'none' },
                '& .PlanCard-pricingPrice': { display: 'block' },
              },
            },
          },
          {
            props: { variant: 'planCardSingle' as string, selected: true },
            style: {
              border: planCardSelectedBorder,
              ...planCardSelectedByCategory,
            },
          },
          {
            props: { variant: 'equipmentCard' },
            style: {
              display: 'flex',
              overflow: 'visible',
              alignItems: 'center',
              gap: tokens.equipmentCardGap,
              padding: tokens.equipmentCardPadding,
              borderRadius: tokens.equipmentCardRadius,
              outline: tokens.equipmentCardBorderDefault,
              backgroundColor: symphonaTheme.colors.white,
              cursor: 'pointer',
              transition: tokens.cardTransition,
              // CPQ Branding -> Equipment Card -> "Hover Border". Without a tenant value
              // this stays the colour-only swap to navy it has always been.
              '&:hover': { outline: hoverOutline(tokens.equipmentCardHoverOutline, tokens.equipmentCardBorderDefault) },
              '&:focus-visible': { outline: hoverOutline(tokens.equipmentCardHoverOutline, tokens.equipmentCardBorderDefault) },
              flexDirection: 'row',
              // SC-12443: three-part row on Tablet/Desktop (image · details ·
              // right-aligned quantity column) reflows to a vertical column
              // (image on top, quantity beneath the description) across BOTH the
              // Bigger-Mobile (sm) and Mobile (xs) bands, per the provided design.
              // R7: reset cross-axis alignment to stretch on the stacked band.
              [tabletDown]: { flexDirection: 'column', alignItems: 'stretch' },
              // Inner-container layout + element colour live here so the card
              // component stays structure-only (no sx, no theme.novus reads).
              // Applied via stable child classNames.
              '& .EquipmentCard-image': {
                flexShrink: 0,
                width: tokens.equipmentImageSize.width,
                height: tokens.equipmentImageSize.height,
                alignItems: 'center',
                justifyContent: 'center',
                [tabletDown]: { alignSelf: 'flex-start' },
                '& img': {
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                },
              },
              '& .EquipmentCard-details': {
                flex: 1,
                minWidth: 0,
              },
              '& .EquipmentCard-priceRow': {
                flexShrink: 0,
                textAlign: 'left',
              },
              '& .EquipmentCard-price': { color: tokens.navy },
              '& .EquipmentCard-message': {
                color: tokens.borderBlack,
                lineHeight: 1.3,
              },
              // SC-12443 quantity column: right-aligned beside the details on
              // Tablet/Desktop, left-aligned beneath them when the card stacks.
              '& .EquipmentCard-quantity': {
                flexShrink: 0,
                alignItems: 'flex-end',
                [tabletDown]: { alignItems: 'flex-start' },
              },
              '& .EquipmentCard-quantityLabel': { color: novusTokens.quantityLabelColor },
            },
          },
          {
            props: { variant: 'equipmentCard', selected: true },
            style: {
              outline: tokens.equipmentCardBorderSelected,
            },
          },
          {
            props: { variant: 'addOnCard' },
            style: {
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible',
              padding: tokens.addOnCardPadding,
              borderRadius: tokens.addOnCardRadius,
              outline: tokens.addOnCardBorderDefault,
              backgroundColor: symphonaTheme.colors.white,
              cursor: 'pointer',
              transition: tokens.cardTransition,
              gap: tokens.addOnCardGap,
              // CPQ Branding -> Add-On Card -> "Hover Border". Falls back to the
              // colour-only navy swap when the tenant has not set one.
              '&:hover': { outline: hoverOutline(tokens.addOnCardHoverOutline, tokens.addOnCardBorderDefault) },
              '&:focus-visible': { outline: hoverOutline(tokens.addOnCardHoverOutline, tokens.addOnCardBorderDefault) },
              // Inner-container layout + element colour live here so the card
              // component stays structure-only (no sx, no theme.novus reads).
              // Applied via stable child classNames.
              '& .AddOnCard-content': {
                flex: 1,
                minWidth: 0,
              },
              '& .AddOnCard-price': { color: tokens.navy },
              '& .AddOnCard-message': {
                color: tokens.borderBlack,
                lineHeight: 1.3,
              },
              '& .AddOnCard-quantityLabel': { color: tokens.quantityLabelColor },
            },
          },
          {
            props: { variant: 'addOnCard', selected: true },
            style: {
              outline: tokens.addOnCardBorderSelected,
            },
          },
          {
            props: { variant: 'themePackageCard' },
            style: {
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible',
              maxHeight: 307,
              padding: '20px',
              borderRadius: '15px',
              boxShadow: tokens.themePackageCardShadow,
              outline: 'none',
              backgroundColor: symphonaTheme.colors.white,
              cursor: 'pointer',
              transition: tokens.cardTransition,
              gap: '20px',
              '&:hover': { outline: tokens.equipmentCardBorderHover },
              '&:focus-visible': { outline: tokens.equipmentCardBorderHover },
              // Inner-container layout + element colour/typography live here so the
              // card component stays structure-only (no sx, no theme.novus reads).
              // Applied via stable child classNames. Literal px are consistent with
              // the variant's existing slice-17 hardcodes.
              '& .ThemePackageCard-content': {
                flex: 1,
                gap: '20px',
                minWidth: 0,
              },
              '& .ThemePackageCard-price': { fontSize: 30, fontWeight: 400 },
              '& .ThemePackageCard-discountTag': { color: tokens.navy },
              '& .ThemePackageCard-discountMessage': {
                color: tokens.borderBlack,
                lineHeight: 1.3,
              },
              '& .ThemePackageCard-logoRow': {
                flexDirection: 'row',
                gap: '10px',
                alignItems: 'center',
                marginTop: 'auto',
              },
              '& .ThemePackageCard-logo': {
                width: 57,
                minWidth: 57,
                aspectRatio: '56.67 / 45',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                borderRadius: '5px',
                border: `1.5px solid ${tokens.headerBorder}`,
                backgroundColor: symphonaTheme.colors.white,
                '&:hover': { borderColor: tokens.navy },
              },
              '& .ThemePackageCard-moreLink': {
                fontFamily: '"Roboto", sans-serif',
                fontSize: '12px',
                fontWeight: 600,
                color: tokens.borderBlack,
                textDecoration: 'underline',
                whiteSpace: 'nowrap',
              },
              [mobileDown]: { maxHeight: 'none' },
            },
          },
          {
            props: { variant: 'themePackageCard', selected: true },
            style: {
              outline: tokens.addOnCardBorderSelected,
            },
          },
          {
            props: { variant: 'individualChannelCard' },
            style: {
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible',
              maxWidth: 192,
              padding: '15px',
              borderRadius: '10px',
              boxShadow: tokens.individualChannelCardShadow,
              outline: 'none',
              backgroundColor: symphonaTheme.colors.white,
              cursor: 'pointer',
              transition: tokens.cardTransition,
              gap: '15px',
              alignItems: 'flex-start',
              '&:hover': { outline: tokens.addOnCardBorderHover },
              '&:focus-visible': { outline: tokens.addOnCardBorderHover },
              // Inner-container layout + logo dimensions + element colour live here so
              // the card component stays structure-only (no sx, no theme.novus reads).
              // Applied via stable child classNames. Literal px are consistent with the
              // variant's existing slice-17 hardcodes.
              '& .IndividualChannelCard-content': {
                flex: 1,
                width: '100%',
              },
              '& .IndividualChannelCard-logo': {
                width: 162,
                height: 115,
              },
              '& .IndividualChannelCard-name': {
                color: tokens.borderBlack,
                textAlign: 'center',
                width: '100%',
              },
              '& .IndividualChannelCard-price': {
                color: tokens.borderBlack,
                textAlign: 'center',
                width: '100%',
              },
              '& .IndividualChannelCard-discountTag': {
                color: tokens.navy,
                textTransform: 'uppercase',
              },
              '& .IndividualChannelCard-discountMessage': {
                color: tokens.borderBlack,
                textAlign: 'center',
                lineHeight: 1.3,
                width: '100%',
              },
              // SC-12441: at 2-up on the Mobile band the cell narrows below the fixed
              // 162px logo, so let the logo fill the cell there while preserving its
              // exact desktop aspect ratio (162×115). Desktop keeps the fixed dims.
              [mobileDown]: {
                '& .IndividualChannelCard-logo': {
                  width: '100%',
                  height: 'auto',
                  aspectRatio: '162 / 115',
                },
              },
            },
          },
          {
            props: { variant: 'individualChannelCard', selected: true },
            style: {
              outline: tokens.addOnCardBorderSelected,
            },
          },
          {
            props: { variant: 'infoBanner' as string },
            style: {
              backgroundColor: tokens.bgCyanLight,
              borderRadius: '10px',
              padding: '16px 20px',
              boxShadow: 'none',
            },
          },
          {
            props: { variant: 'quantityCounter' as string },
            style: {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              border: tokens.quantityCounterBorder,
              borderRadius: tokens.quantityCounterRadius,
              overflow: 'hidden',
              width: 66,
              height: 36,
              // Card ships a default elevation shadow + paper fill; null them so the
              // migrated counter matches the bare styled Stack it replaces.
              boxShadow: 'none',
              backgroundColor: 'transparent',
              // Cell layout + element colour live here so the counter component stays
              // structure-only (no sx, no theme.novus reads). Applied via stable child
              // classNames.
              '& .QuantityCounter-button': {
                width: '25%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                userSelect: 'none',
                backgroundColor: 'transparent',
                '&:hover': { backgroundColor: tokens.headerBorder },
                '& .MuiSvgIcon-root': { fontSize: 16 },
              },
              '& .QuantityCounter-value': {
                flex: 1,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                userSelect: 'none',
              },
              // The value digit's borderBlack colour is NOT set here: an ancestor→
              // descendant colour rule does not surface on the child Typography's
              // computed style (it loses to the default Typography colour prop). It is
              // applied on the instance via `&&` in the MuiTypography styleOverrides,
              // scoped to the QuantityCounter-valueText class — same pattern as
              // InfoBanner-text. See theme.ts.
            },
          },
        ],
      },
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: {
        // LeadCaptureForm hairline rule, replacing the per-instance `sx={{ height: '1.5px' }}`
        // the four dividers carried. A className seam (sibling of the variants array, the
        // MC-6/MC-9 pattern) rather than a custom variant so the default divider colour/border
        // is untouched. Opt-in via className="lead-capture-divider".
        '&.lead-capture-divider': { height: '1.5px' },
        // LeadCaptureForm spaced modifier, replacing the extra `mt: '20px'` the Customer-Type
        // divider carried alongside the base height. Composed onto the base class so the height
        // comes from -divider and the top gap from this modifier. Opt-in via
        // className="lead-capture-divider-spaced".
        '&.lead-capture-divider-spaced': { marginTop: '20px' },
        variants: [
          {
            props: { variant: 'planSection' as string },
            style: {
              borderColor: tokens.bgCyanLight,
              borderWidth: '2px',
              // Vertical dividers only exist in the single-plan row layout, so they
              // reveal at the same Tablet boundary the card switches to row (md).
              height: 0,
              overflow: 'hidden',
              [tabletUp]: { height: '2px' },
            },
          },
          {
            props: { variant: 'planDetailsRule' as string },
            style: {
              borderWidth: '1.5px',
              borderColor: tokens.highlightIconColor,
              marginBlock: '8px',
            },
          },
          {
            props: { variant: 'cartSection' as string },
            style: {
              borderColor: tokens.cartSectionDivider,
              borderWidth: '2px',
              margin: '30px 0',
            },
          },
          {
            // Hairline section divider inside the Checkout OrderSummary panel. Replaces
            // the per-instance DIVIDER_SX the four dividers carried (cyan 0.5px rule with
            // a horizontal inset that widened from the Bigger-Mobile band up). The inset
            // uses an explicit smUp @media key because object xs/sm breakpoint syntax does
            // NOT resolve inside a theme style object — it reproduces the old
            // mx:{xs:'16px',sm:'24px'} byte-identically. Additive: opt-in via
            // variant="orderSummary".
            props: { variant: 'orderSummary' as string },
            style: {
              borderColor: tokens.accentCyan,
              borderWidth: '0.5px',
              marginInline: '16px',
              [smUp]: { marginInline: '24px' },
            },
          },
        ],
      },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: {
        // Left-aligned inline alert for the active-account and system-error views of the
        // CheckAvailabilityDialog / AddressForm (the InlineAlert styled(Alert) it replaces).
        // A className seam rather than a custom variant so the default `standard` variant +
        // `severity` still paint the warning-amber / error-tint background — that fill is the
        // whole point of keeping severity, which a custom variant would override. All plain
        // literals — no brand hex to consolidate. Opt-in via className="check-availability-inline-alert".
        '&.check-availability-inline-alert': {
          textAlign: 'left',
          fontFamily: "'Roboto', sans-serif",
          fontSize: 14,
          borderRadius: '10px',
        },
        variants: [
          {
            props: { variant: 'checkoutError' as string },
            style: {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              padding: '15px',
              gap: '10px',
              backgroundColor: tokens.errorBannerBg,
              color: tokens.errorRed,
              fontFamily: 'Agrandir',
              fontWeight: 500,
              fontSize: '19px',
              lineHeight: '130%',
              borderRadius: 0,
              boxShadow: 'none',
              '& .MuiAlert-icon': {
                display: 'none',
              },
              '& .MuiAlert-message': {
                padding: 0,
                overflow: 'visible',
              },
              '& .MuiAlert-action': {
                display: 'none',
              },
            },
          },
        ],
      },
    },
  },

  MuiTooltip: {
    defaultProps: {
      arrow: true,
    },
    styleOverrides: {
      tooltip: {
        backgroundColor: tokens.borderBlack,
        color: tokens.sectionBgWhite,
        padding: 10,
        borderRadius: 6,
        fontFamily: 'Roboto',
        fontWeight: 500,
        fontSize: 16,
        lineHeight: '100%',
        letterSpacing: 0,
      },
      arrow: {
        color: tokens.borderBlack,
        fontSize: 14,
        '&::before': {
          borderRadius: 3,
        },
      },
    },
  },

  MuiFab: {
    styleOverrides: {
      root: {
        variants: [
          {
            props: { variant: 'contact' as string },
            style: {
              position: 'fixed',
              right: tokens.novusFabOffset,
              bottom: `calc(${tokens.novusFooterHeight} + ${tokens.novusFabOffset}px)`,
              zIndex: 1101,
              width: tokens.novusFabSize,
              height: tokens.novusFabSize,
              fontSize: 29,
              backgroundColor: tokens.primaryBrandBlue,
              color: tokens.sectionBgWhite,
              boxShadow: tokens.cardBoxShadow,
              '&:hover': {
                backgroundColor: tokens.selectPlanHoverBg,
              },
              '&:focus-visible': {
                outline: `2px solid ${tokens.accentCyan}`,
                outlineOffset: 2,
              },
              '&.Mui-disabled': {
                backgroundColor: tokens.checkoutDisabledBg,
                color: tokens.sectionBgWhite,
              },
              '@media (max-width: 599.95px)': {
                bottom: `calc(${tokens.novusFooterHeight} + env(safe-area-inset-bottom, 0px) + ${tokens.novusFabOffset}px)`,
              },
            },
          },
        ],
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      root: {
        // CheckAvailabilityDialog seam, className-keyed (NOT a Dialog `variant`) so it
        // can carry both the content insets and the centred-card paper treatment on one
        // rule. Per Figma this dialog is a CENTRED rounded card at every width and never
        // goes full-page — so the paper takes a 25px radius (matching the other centred
        // dialogs) and a 30px-margin card on mobile (mirrors `warning`), instead of the
        // `fullPageMobile` variant it used to carry. `& .MuiDialogContent-root` padding 0
        // replaces the form's `<DialogContent sx={{ p: 0 }}>`; `& .check-availability-content`
        // padding '16px 24px 0' replaces the banner/callout wrapper `<Stack sx={{ px: 3,
        // pt: 2 }}>` (24px L/R, 16px top, 0 bottom). Additive: a dialog opts in with
        // className="check-availability-dialog".
        '&.check-availability-dialog': {
          '& .MuiDialog-paper': {
            borderRadius: '25px',
            [mobileDown]: {
              margin: '30px',
              maxWidth: 'calc(100vw - 60px)',
            },
          },
          '& .MuiDialogContent-root': { padding: 0 },
          '& .check-availability-content': { padding: '16px 24px 0' },
        },
        // Resume-order ("Your Cart is Ready When You Are") dialog seam (slice-21d MC-6).
        // Folds three InProgressOrderModal instance literals onto its `variant="warning"`
        // Dialog into one className seam so the warning variant's own `@media (max-width:
        // 480px)` mobile collapse can win below the Mobile band. The 470px paper floor is
        // confined to the exact COMPLEMENT (`min-width: 480.05px`) so desktop stays at
        // 470px and the two queries never overlap; the close icon sizes under the existing
        // `.warning-dialog-close` wrapper; the action row flips to a full-width column at
        // the same mobile boundary. Additive: opt in via className="resume-order-dialog".
        '&.resume-order-dialog': {
          '& .MuiDialog-paper': {
            '@media (min-width: 480.05px)': {
              minWidth: '470px',
              maxWidth: '470px',
            },
          },
          '& .warning-dialog-close .MuiSvgIcon-root': {
            fontSize: 24,
          },
          '& .resume-order-actions': {
            '@media (max-width: 480px)': {
              flexDirection: 'column',
              width: '100%',
              '& > *': {
                width: '100%',
              },
            },
          },
        },
        // LeadCaptureDialog ("Customer Intake Form") paper-sizing seam (slice-21d MC-7).
        // Folds the dialog's former inline PaperProps.sx onto an UNCONDITIONAL className
        // rule (NOT a Dialog `variant`) so it is byte-identical at every width — there is
        // no media query, the `maxWidth: '100%'` cap alone clamps the 775px paper below
        // 775px. The `sx` shorthand `bgcolor` becomes the real CSS `backgroundColor` since
        // styleOverrides skip MUI's sx transform. Additive: opt in via
        // className="lead-capture-dialog".
        '&.lead-capture-dialog': {
          '& .MuiDialog-paper': {
            backgroundColor: tokens.dialogBg,
            '@media (min-width: 481px)': {
              borderRadius: '25px',
              width: '775px',
              maxWidth: '100%',
              height: '80vh',
            },
          },
          '& .MuiDialogContent-root': {
            padding: 0,
            overflowY: 'auto',
          },
          '& .MuiDialogActions-root': {
            padding: '20px 30px',
            boxShadow: tokens.dialogActionsShadow,
            zIndex: 1,
            flexDirection: 'column',
            gap: '20px',
            '@media (max-width: 480.95px)': { padding: '20px' },
          },
          '& .lead-capture-actions': {
            '@media (max-width: 480.95px)': {
              flexDirection: 'column',
              width: '100%',
              '& > *': { width: '100%' },
            },
          },
          '& .lead-capture-error-alert': {
            width: '100%',
            borderRadius: '10px',
            padding: '7px 15px',
            boxSizing: 'border-box',
          },
        },
        '&.lead-capture-confirmation-dialog': {
          '& .MuiDialog-paper': { borderRadius: '25px', paddingBottom: '20px' },
          '& .MuiDialogContent-root': { padding: 0 },
          '& .lead-capture-confirmation-actions': {
            '@media (max-width: 480.95px)': {
              width: '100%',
              '& > *': { width: '100%' },
            },
          },
        },
        variants: [
          {
            props: { variant: 'configurePlanDialog' as string },
            style: {
              '& .MuiDialog-container': {
                alignItems: 'center',
              },
              '& .MuiDialog-paper': {
                maxWidth: tokens.dialogMaxWidth,
                maxHeight: tokens.dialogMaxHeight,
                height: `calc(100% - 64px)`,
                borderRadius: tokens.dialogBorderRadius,
                backgroundColor: tokens.dialogBg,
                width: '100%',
              },
              '& .MuiDialogTitle-root': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: tokens.dialogHeaderHeight,
                padding: tokens.dialogSectionPadding,
                borderBottom: tokens.dialogSectionBorder,
                flexShrink: 0,
              },
              '& .MuiDialogContent-root': {
                display: 'flex',
                flexDirection: 'column',
                padding: tokens.dialogContentPadding,
                gap: tokens.dialogContentGap,
                overflow: 'auto',
              },
              '& .MuiDialogActions-root': {
                justifyContent: 'space-between',
                height: tokens.dialogFooterHeight,
                padding: tokens.dialogSectionPadding,
                borderTop: tokens.dialogSectionBorder,
                flexShrink: 0,
              },
              '& .MuiDialogActions-root.footer-end': {
                justifyContent: 'flex-end',
              },
              [mobileDown]: {
                // SC-12441: pin the container to the VISIBLE viewport (dvh) so the
                // full-bleed paper's pinned footer isn't hidden behind Chrome mobile's
                // dynamic bottom toolbar (which inflates 100%/vh to the large viewport).
                '& .MuiDialog-container': {
                  height: '100dvh',
                },
                '& .MuiDialog-paper': {
                  width: '100%',
                  maxWidth: '100%',
                  height: '100%',
                  maxHeight: '100%',
                  margin: 0,
                  borderRadius: 0,
                },
                '& .MuiDialogContent-root': {
                  padding: '20px',
                  // The add-on tab strip (addOnTabs, -30px bleed) and the scrollable
                  // card region (ScrollableContent, -24px bleed) full-bleed to the paper
                  // edge via negative margins sized to the desktop 30px content padding.
                  // Against the 20px mobile padding those margins overshoot the paper by
                  // ~10px/4px, which surfaced as a horizontal scrollbar. Clip the bleed
                  // here so the full-page mobile dialog never scrolls sideways; the tab
                  // labels and cards are inset and unaffected.
                  overflowX: 'hidden',
                },
              },
              // SC-12441: on every band below Desktop (lg) the footer stacks — the
              // count keeps its own line above a compact, right-aligned button row
              // (not a tall full-width vertical stack). Only true desktop keeps the
              // single-row space-between footer. The released `height: auto` needs its
              // own vertical padding so the buttons are not flush against the viewport
              // bottom — the breathing room the fixed-height equipment footer gets for
              // free (SC-12441 O1: "still cut off").
              [belowDesktop]: {
                '& .MuiDialogActions-root:not(.footer-end)': {
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  height: 'auto',
                  padding: novusTokens.dialogFooterMobilePadding,
                  gap: novusTokens.dialogFooterMobileGap,
                  '& > :not(:first-of-type)': {
                    marginLeft: 0,
                  },
                  '& .MuiStack-root': {
                    width: '100%',
                    justifyContent: 'flex-end',
                  },
                },
              },
              // Landscape / short viewport: the fixed header + sub-heading + tabs +
              // filter + footer eat the whole height and collapse the scrollable list
              // to zero. Become a full-height sheet, drop the redundant sub-heading
              // (first child — a heading in every step; the title already names the
              // step), and hold the list (last child) at its natural height so the
              // content region scrolls instead of the list collapsing. Keep the flex
              // column so the 16px inter-band gap survives. Shared by every configure
              // step (equipment + add-ons) for one consistent landscape treatment.
              [shortViewportDown]: {
                '& .MuiDialog-container': {
                  height: '100dvh',
                },
                '& .MuiDialog-paper': {
                  height: '100%',
                  maxHeight: '100%',
                  margin: '0 auto',
                  borderRadius: 0,
                },
                '& .MuiDialogContent-root': {
                  overflowY: 'auto',
                },
                '& .MuiDialogContent-root > :first-of-type': {
                  display: 'none',
                },
                '& .MuiDialogContent-root > :last-of-type': {
                  flex: '1 0 auto',
                },
                // Undo the belowDesktop footer stack: landscape has the horizontal
                // room for the summary and buttons on one row, and reclaiming that
                // vertical space matters more when height is scarce. flexWrap keeps a
                // graceful fallback — the buttons drop to their own line only if a
                // viewport really is too narrow to fit them beside the summary.
                '& .MuiDialogActions-root:not(.footer-end)': {
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  height: 'auto',
                  gap: novusTokens.dialogFooterMobileGap,
                  '& .MuiStack-root': {
                    width: 'auto',
                  },
                },
              },
            },
          },
          {
            props: { variant: 'channelListDialog' as string },
            style: {
              '& .MuiDialog-container': {
                alignItems: 'center',
              },
              '& .MuiDialog-paper': {
                width: '100%',
                maxWidth: tokens.sideModalWidth,
                borderRadius: tokens.dialogBorderRadius,
                backgroundColor: tokens.dialogBg,
              },
              '& .MuiDialogContent-root': {
                padding: '30px 20px',
              },
              [mobileDown]: {
                // SC-12441: see configurePlanDialog — pin to the visible viewport (dvh).
                '& .MuiDialog-container': {
                  height: '100dvh',
                },
                '& .MuiDialog-paper': {
                  width: '100%',
                  maxWidth: '100%',
                  height: '100%',
                  maxHeight: '100%',
                  margin: 0,
                  borderRadius: 0,
                },
              },
            },
          },
          {
            props: { variant: 'warning' as string },
            style: {
              '& .MuiDialog-paper': {
                borderRadius: '25px',
                minWidth: 528,
                boxShadow: tokens.warningDialogShadow,
                [tabletDown]: {
                  minWidth: 'unset',
                  maxWidth: 'calc(100vw - 60px)',
                  margin: '30px',
                },
              },
              '& .warning-dialog-content': {
                alignItems: 'center',
                padding: 'clamp(28px, 6vw, 36px) clamp(20px, 5vw, 36px) clamp(24px, 5vw, 32px)',
                gap: '16px',
                textAlign: 'center',
                position: 'relative',
              },
              '& .warning-dialog-close': {
                position: 'absolute',
                top: 16,
                right: 16,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 32,
                minHeight: 32,
                color: tokens.borderBlack,
              },
              '& .warning-dialog-icon': {
                alignItems: 'center',
                justifyContent: 'center',
                color: tokens.warningDialogIconColor,
              },
              '& .warning-dialog-actions': {
                flexWrap: 'wrap',
                gap: '12px',
                width: '100%',
                justifyContent: 'center',
                paddingTop: '8px',
                '& > button': {
                  minWidth: 'clamp(100px, 40vw, 200px)',
                },
                [mobileDown]: {
                  '& > button': {
                    minWidth: '100%',
                  },
                },
              },
            },
          },
          {
            props: { 'data-backdrop-variant': 'blurred' as string },
            style: {
              '& .MuiBackdrop-root': {
                backdropFilter: tokens.modalBackdropBlur,
                WebkitBackdropFilter: tokens.modalBackdropBlur,
              },
            },
          },
          // R10: shared opt-in full-page-on-mobile mechanism. A dialog adds
          // `variant="fullPageMobile"` to make its paper fill the viewport below the
          // Mobile band and step its content padding 30 -> 20. This is a SEPARATE
          // variants entry from `warning`/`configurePlanDialog`, so it never alters
          // their centred-card mobile look — only opted-in surfaces (21b/c/d) get it.
          {
            props: { variant: 'fullPageMobile' as string },
            style: {
              [mobileDown]: {
                // SC-12441: see configurePlanDialog — pin to the visible viewport (dvh).
                '& .MuiDialog-container': {
                  height: '100dvh',
                },
                '& .MuiDialog-paper': {
                  width: '100%',
                  maxWidth: '100%',
                  height: '100%',
                  maxHeight: '100%',
                  margin: 0,
                  borderRadius: 0,
                },
                '& .MuiDialogContent-root': {
                  padding: '20px',
                },
              },
            },
          },
        ],
      },
    },
  },

  // R10: net-new MuiDrawer override (no MuiDrawer key existed before). The
  // `fullPageMobile` opt-in flag gives a right-anchored drawer a full-width paper
  // on the Mobile band, modelled on the cart/side-modal PaperProps width
  // `{ xs: '100%', sm: <fixed> }`. It is a NEW boolean own-prop (not MUI's closed
  // `variant` union, which can't be widened), so existing drawers — which set their
  // own paper width via PaperProps and never pass this flag — are unaffected.
  // 21b applies it to the cart/side-modal.
  MuiDrawer: {
    styleOverrides: {
      root: {
        variants: [
          {
            props: { fullPageMobile: true },
            style: {
              '& .MuiDrawer-paper': {
                width: tokens.sideModalWidth,
                [mobileDown]: {
                  width: '100%',
                },
              },
            },
          },
        ],
      },
    },
  },

  // ── Date Picker ────────────────────────────────────────────────────────

  MuiPickersPopper: {
    styleOverrides: {
      paper: {
        borderRadius: tokens.equipmentCardRadius,
        boxShadow: tokens.cardBoxShadow,
        backgroundColor: tokens.sectionBgWhite,
        margin: '4px',
      },
    },
  },

  MuiPickersLayout: {
    styleOverrides: {
      root: {
        backgroundColor: tokens.sectionBgWhite,
        color: tokens.primaryBrandBlue,
      },
    },
  },

  MuiPickersCalendarHeader: {
    styleOverrides: {
      root: {
        color: tokens.primaryBrandBlue,
      },
      label: {
        color: tokens.primaryBrandBlue,
        fontWeight: 600,
      },
      switchViewButton: {
        color: tokens.primaryBrandBlue,
        '&:hover': {
          backgroundColor: tokens.bgCyanLighter,
        },
      },
    },
  },

  MuiPickersArrowSwitcher: {
    styleOverrides: {
      button: {
        color: tokens.primaryBrandBlue,
        '&:hover': {
          backgroundColor: tokens.bgCyanLighter,
        },
        '&.Mui-disabled': {
          opacity: 0.5,
        },
      },
    },
  },

  MuiDayCalendar: {
    styleOverrides: {
      weekDayLabel: {
        opacity: 0.5,
        fontWeight: 600,
        fontSize: '14px',
      },
    },
  },

  MuiPickersDay: {
    styleOverrides: {
      root: {
        color: tokens.primaryBrandBlue,
        fontWeight: 500,
        fontSize: '14px',
        '&:hover': {
          backgroundColor: tokens.bgCyanLighter,
        },
        '&:focus': {
          backgroundColor: tokens.bgCyanLight,
        },
        '&.Mui-selected': {
          backgroundColor: tokens.primaryBrandBlue,
          color: tokens.sectionBgWhite,
          '&:hover, &:focus': {
            backgroundColor: tokens.selectPlanHoverBg,
            color: tokens.sectionBgWhite,
          },
        },
        '&:not(.Mui-selected).MuiPickersDay-today': {
          border: `1px solid ${tokens.accentCyanBright}`,
        },
        '&.Mui-disabled': {
          opacity: 0.5,
        },
      },
    },
  },

  MuiYearCalendar: {
    styleOverrides: {
      root: {
        color: tokens.primaryBrandBlue,
      },
    },
  },

  MuiPickersYear: {
    styleOverrides: {
      yearButton: {
        color: tokens.primaryBrandBlue,
        borderRadius: tokens.buttonRadius,
        '&:hover': {
          backgroundColor: tokens.bgCyanLighter,
        },
        '&.Mui-selected': {
          backgroundColor: tokens.primaryBrandBlue,
          color: tokens.sectionBgWhite,
          '&:hover, &:focus': {
            backgroundColor: tokens.selectPlanHoverBg,
          },
        },
        '&.Mui-disabled': {
          opacity: 0.5,
        },
      },
    },
  },

  MuiMonthCalendar: {
    styleOverrides: {
      root: {
        color: tokens.primaryBrandBlue,
      },
    },
  },

  MuiPickersMonth: {
    styleOverrides: {
      monthButton: {
        color: tokens.primaryBrandBlue,
        borderRadius: tokens.buttonRadius,
        '&:hover': {
          backgroundColor: tokens.bgCyanLighter,
        },
        '&.Mui-selected': {
          backgroundColor: tokens.primaryBrandBlue,
          color: tokens.sectionBgWhite,
          '&:hover, &:focus': {
            backgroundColor: tokens.selectPlanHoverBg,
          },
        },
        '&.Mui-disabled': {
          opacity: 0.5,
        },
        '&.Mui-disabled:not(.Mui-selected)': {
          opacity: 0.5,
        },
      },
    },
  },
  };
};
